/**
 * ⭐ THE INVOICES TAB (T1011 M8) — and it is THE PRODUCT, not a screen.
 *
 * WHAT THIS IS FOR. The €12,500 that started this build was invoiced and
 * recoverable the whole time. What was missing was any way to SEE what was
 * outstanding without reconstructing it by hand from a third-party overdue list.
 * So the outstanding view comes FIRST on this pane and the assembly queue second:
 * the queue is the supporting half, and this is the half that answers the
 * question nobody in the business could answer.
 *
 * ⛔ EVERYTHING HERE IS PAYER KEYED, NEVER SITE KEYED, and that closes a measured
 * divergence rather than expressing a preference. The old site-keyed preview
 * (`GET /api/account-billables`, removed at board 2764 M6) was keyed on ONE SITE
 * while the issue path is keyed on the PAYER, so a screen built naively on it
 * showed one home's total and minted the whole parent's document - the operator
 * confirms one figure and twice it goes out. `/api/account-billing-rollup`
 * answers at the grain the ledger bills at.
 *
 * ⭐ board 2764 M6 (R-12): THIS PANE RENDERS NO INVOICE DOCUMENT OF ITS OWN. Each
 * sent invoice offers Stripe's PDF, streamed by this kiosk; the "N runs" table of
 * frozen lines that stood in that column was ECMB's own rendering and is gone.
 *
 * ⛔ MONEY GOES THROUGH `euro()`, NEVER A LOCAL FORMATTER. `PortalRequests.tsx`
 * carries a divergent one that forces two decimals and drops en-IE grouping, so
 * the same amount would render differently two tabs apart on one screen.
 *
 * ⛔ AND AN UNPRICED RUN IS NEVER COUNTED AS ZERO. billables-repo keeps three
 * price states apart because "no quote row" and "a quote row with no figure" have
 * different remedies; folding either into a total tells Helen a month is fully
 * priced and hands her a figure short by exactly the runs nobody has quoted. The
 * total says it is incomplete instead.
 *
 * ⛔⛔ AND THE QUEUE ISSUES. M8 shipped this pane with the queue card's headline,
 * evidence, total and a disclosure toggle, and NO issue action - so
 * `issueAccountInvoice`, `POST /api/account-invoices` and their several hundred
 * passing tests had no reachable UI at all, and the build's core deliverable
 * could only be exercised with curl. Every test that would have caught it drove
 * the service or the route DIRECTLY. ⇒ The grading spec for this control
 * (test/client/account-invoices-tab.spec.tsx) clicks the RENDERED button and
 * asserts the payload, because a test that calls the action function discharges
 * nothing.
 *
 * ⛔⛔ AND IT SHOWS THE MONTH THAT CAN ACTUALLY BE INVOICED, WHICH IS THE LAST
 * COMPLETED ONE. Wiring the button was not enough: `fetchBillingRollUp` sent no
 * `month`, so the queue was always the CURRENT month, and `issueAccountInvoice`
 * refuses any period that has not ended - so the control Helen could finally see
 * was disabled in every state production can reach. The same "true of the
 * function, false of the system" shape as the missing button, one layer up. You
 * invoice August during September, so this pane opens on the month BEFORE the
 * server's own, and says which month that is on screen.
 *
 * ⛔ THE CLOCK IS STILL THE SERVER'S. The month key is derived from a roll-up
 * response's resolved `{year, month}` - the first read deliberately asks for no
 * month so the server states its own, and the second asks for the one before it.
 * Two reads, and never a `new Date()` in this file.
 *
 * ⚠️ THIS PANE IS DATA-SCALED, so like `NursingHomes.tsx` it is deliberately
 * absent from test/client/control-budget.spec.tsx's DEBT enumeration: its control
 * count is a function of how many payers, invoices and warnings exist, and a DEBT
 * row must record an EXACT number. The resting count is held down the way that
 * file does it — every per-row detail is REVEALED rather than always-open, and
 * exactly ONE control on the pane is a dominant primary action.
 */
import { useEffect, useState } from 'preact/hooks';
import { Button, EmptyState } from '@ecmb/ui';
import {
  accountInvoicePdfHref,
  downloadAccountInvoicePdf,
  fetchBillingRollUp,
  fetchInvoiceAtStripe,
  issueAccountInvoiceForMonth,
  markAccountInvoicePaidByOperator,
  resendAccountInvoice,
  setBookingPayerKind,
  setBookingSeriesActive,
  topUpOccurrences,
  voidAccountInvoice,
  type ApiAccount,
  type ApiAssemblyRow,
  type ApiBillingRollUp,
  type ApiInvoiceAtStripe,
  type ApiInvoiceKind,
  type ApiIssuedMonth,
  type ApiOutstandingRow,
  type ApiPayerInvoice,
  type ApiResidentLabel,
  type ApiRunWithoutPayer,
  type ApiSeriesShortfall,
  type ApiUninvoicedRun,
} from '../../lib/actions.js';
import { civilDateLabel, euro, shortDate } from '../../lib/format.js';
import { PAYER_WORDS } from '../../lib/payer-words.js';
import { Banner } from '../../components/Banner.js';
import { ApiError } from '../../lib/api.js';

export type AccountInvoicesProps = {
  /**
   * The customer book, already read by the parent. Passed rather than re-fetched:
   * a warning row carries an `accountId` and no name, deliberately (a closed
   * vocabulary crosses the wire, never an organisation), and this is where the
   * name is joined back on.
   *
   * `null` means the parent's read FAILED, which is a different fact from an
   * empty book and is rendered as one.
   */
  accounts: ApiAccount[] | null | undefined;
  /**
   * ⭐ board 2812 M4 (plan step 3): a home's page shows THIS account's invoices only
   * (the payer it bills to, its own runs and warnings), read through the roll-up's
   * account filter on the server. Absent: every home, as before (All homes' Invoices).
   */
  accountId?: number;
  /** ⭐ board 2812 M4 fix round (C2): the account is unknown to the server (404), so the
   *  host falls back to All homes. Read only with `accountId`. */
  onStale?: () => void;
};

/** Month names, spelled out. A period is read aloud by Helen on the phone to a
 *  home's accounts office, so "August 2026" beats "2026-08". */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function periodWords(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1] ?? String(month)} ${String(year)}`;
}

/**
 * ⭐⭐ THE AMOUNT HELEN TYPED, AS WHOLE CENTS — or `null` if it is not a figure.
 *
 * ⛔⛔ THE UNIT IS THE WHOLE HAZARD, WHICH IS WHY THIS IS A NAMED, EXPORTED,
 * SEPARATELY PINNED FUNCTION RATHER THAN AN EXPRESSION AT THE CALL SITE.
 * €1,250.00 typed into a field that means CENTS is €12.50 — and the server
 * compares the figure to the frozen total EXACTLY, so on some invoice a unit slip
 * will MATCH BY COINCIDENCE rather than reject, and one home's payment settles
 * another's document. The field is therefore labelled in EURO, this is the ONE
 * conversion, and the route accepts only the minor-unit integer.
 *
 * ⛔ NO FLOATING-POINT MULTIPLY. `Math.round(Number('12.10') * 100)` happens to
 * work and `Number('1.005') * 100` does not; both halves are parsed as INTEGERS
 * and combined, so the answer is exact for every input this accepts.
 *
 * ⛔ A CLOSED GRAMMAR, AND EVERYTHING ELSE IS `null` RATHER THAN A GUESS:
 *   - an optional leading `€` and ordinary spaces, because Helen pastes from a
 *     bank statement;
 *   - `,` as a THOUSANDS separator only, and `.` as the ONLY decimal point.
 *     ⚠️ `1,25` is REFUSED rather than read as €1.25 — en-IE writes €1.25, and a
 *     silent continental reading would turn €1,250 into €1.25 with no symptom.
 *   - at most two decimal places. Three is a typo, not a third of a cent.
 *   - zero and negatives are refused: an issued invoice always bills something,
 *     so a zero receipt is a mistype and `amount_mismatch` would be the wrong
 *     diagnosis for it.
 */
export function centsFromTypedEuro(raw: string): number | null {
  const t = raw.trim().replace(/^€/, '').replace(/\s/g, '');
  if (!/^\d{1,3}(,\d{3})*(\.\d{1,2})?$|^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ''] = t.replace(/,/g, '').split('.');
  const cents = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents) || cents <= 0) return null;
  return cents;
}

/** A withdrawal's own words. ⛔ `not-attempted` is NOT "it worked at Stripe": the
 *  document never reached Stripe, so there was nothing to take back there — and
 *  telling Helen a copy had been pulled out of a payer's inbox that was never
 *  sent to it is the same class of falsehood `issuedWords` exists to prevent. */
function withdrawnWords(r: { invoiceNumber: string; stripeWithdrawal: string }): string {
  const at: Record<string, string> = {
    'not-attempted': 'It had never been sent to the home, so there was nothing to take back from them.',
    'draft-deleted': 'The copy at Stripe has been removed, so the home cannot be asked for it.',
    voided: 'The copy at Stripe has been cancelled, so the home can no longer pay it. ⚠️ If it had already been emailed, that email is still in their inbox, but it will not take payment.',
    'already-withdrawn': 'The copy at Stripe was already gone.',
    // board 2764 M5: a nothing-due invoice. Stripe marks it settled at EUR 0 and
    // will not cancel it, and it never asked the home for money.
    'nil-settled': 'It had nothing due, so the home was never asked to pay it. Stripe keeps its copy marked as settled at no charge.',
  };
  return `Invoice ${r.invoiceNumber} has been withdrawn. ${at[r.stripeWithdrawal] ?? ''} `
    + 'The runs on it can be billed again, and its number stays used for ever, so the month will be issued under a new one.';
}

/** What Stripe says, in Helen's words. ⛔ The `stillPayable` verdict is the
 *  SERVER's, never re-derived here: two definitions of "can this still be paid"
 *  is how a screen and a runbook end up disagreeing. */
function stripeWords(s: ApiInvoiceAtStripe): string {
  if (!s.sentToStripe) {
    return 'This invoice was never sent to the home through Stripe, so Stripe holds nothing for it. Nobody has been asked to pay it.';
  }
  const settled = s.amountPaidCents > 0
    ? `Stripe has ${euro(s.amountPaidCents)} down as paid against it.`
    : 'Stripe has nothing down as paid against it.';
  return s.stillPayable
    ? `The home can still pay this at Stripe, and Stripe is still chasing it. ${settled}`
    : `The home can no longer pay this at Stripe. ${settled}`;
}

/**
 * The month AFTER this one. Its first day is the day the period becomes
 * issuable, because `issueAccountInvoice` refuses any month whose
 * `monthEndExclusive` today has not reached.
 *
 * ⛔ PURE ARITHMETIC ON THE SERVER'S OWN NUMBERS, NEVER `new Date()`. The
 * comparison it feeds decides whether Helen is offered a live button, and the
 * server owns the clock: `year`, `month` and `computedOn` all come from ONE
 * Dublin reading in the roll-up handler, so a browser in another offset cannot
 * reach a different verdict from the one the route would.
 */
function monthAfter(year: number, month: number): { year: number; month: number } {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

/**
 * The month BEFORE this one, which is the last one that can have ended.
 *
 * ⛔ PURE ARITHMETIC ON THE SERVER'S OWN NUMBERS, NEVER `new Date()`, for the
 * reason `monthAfter` gives and one more: this decides which month the screen
 * ASKS FOR, so a browser-derived answer would let two machines in different
 * offsets assemble two different queues on the last/first of a month. ⚠️ The
 * January case is the one that is silently wrong if this is written as
 * `month - 1`: in January the last completed month is DECEMBER OF LAST YEAR.
 */
function monthBefore(year: number, month: number): { year: number; month: number } {
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
}

/** 'YYYY-MM' - the only form `/api/account-billing-rollup` and
 *  `POST /api/account-invoices` parse; both 400 on anything else. */
function monthKeyOf(year: number, month: number): string {
  return `${String(year)}-${String(month).padStart(2, '0')}`;
}

/** Civil 'YYYY-MM-DD' for the first day of a month. Comparable to `computedOn`
 *  as a plain string: both are civil dates in the same fixed format. */
function firstDayIso(year: number, month: number): string {
  return `${String(year)}-${String(month).padStart(2, '0')}-01`;
}

/**
 * What ONE issued document says to the operator.
 *
 * ⛔⛔ THE SEND OUTCOME IS REPORTED EVERY TIME, IN BOTH DIRECTIONS, and that is
 * this function's reason for existing. The issue route deliberately never fails
 * over a Stripe outage: the document EXISTS the moment the ledger row commits,
 * so a refusal to send rides out as `sentToStripe: false` on a 200. Rendering
 * only the invoice number would report one field of a multi-field outcome and
 * tell Helen a home had been asked for money that nobody has asked it for - the
 * same shape of defect as `onTopUp` reporting `created` without `withdrawn`.
 *
 * ⭐ AND THE NOT-SENT ARM NOW NAMES A REMEDY, BECAUSE THERE FINALLY IS ONE. It
 * used to end "Tell Alex, so it can be sent": `POST /api/account-invoices/:id/send`
 * existed, was tested, and NOTHING on any screen called it, so the only sentence
 * that could honestly be written was one Helen had to leave the product to act
 * on. The banner carries the send control beside these words now, so the state
 * this pane reports is the state this pane can clear.
 *
 * ⭐ board 2764 M7 - EVERY INVOICE THE ISSUE CREATED, FROM `invoices`. A month is
 * now a pair (or one invoice, or the one kind still missing after a void), and
 * the old top-level fields described the FIRST invoice only: on a house-only
 * month that was the nil HSE one, so this read "EUR 0 across 0 runs" while the
 * house invoice had gone out. Each invoice gets its own number, segment, amount
 * and send outcome. ⛔ It never names "the other invoice": a frozen companion
 * number can point at a withdrawn one (see `ApiPayerInvoice.payerKind`).
 */
function issuedWords(r: ApiIssuedMonth, payerName: string, period: string): string {
  const said: string[] = [
    `${r.invoices.length === 1 ? 'Invoice' : `${String(r.invoices.length)} invoices`} raised for ${payerName} for ${period}.`,
  ];
  for (const inv of r.invoices) {
    said.push(inv.isNil
      ? `${inv.invoiceNumber} (${kindLabel(inv.payerKind)}): there were no HSE / Rehab trips, so it asks for nothing.`
      : `${inv.invoiceNumber} (${kindLabel(inv.payerKind)}): ${euro(inv.totalCents)} across ${runsWord(inv.lineCount)}.`);
    if (inv.sentToStripe) {
      said.push(inv.isNil ? 'It has been sent to the home.' : 'It has been sent to the home for payment.');
    } else {
      said.push(inv.isNil
        ? 'It has not been sent to the home yet. The invoice itself is safe and keeps its number.'
        : 'It has not been sent to the home yet, so nobody has been asked to pay it. The invoice itself is safe and keeps its number.');
    }
    if (!inv.ourNumberIsTheReference) {
      // ⚠️ ACTIONABLE, NOT TRIVIA: it went out quoting somebody else's reference,
      // so a bank transfer arriving for it will not mention our number, which is
      // what Helen matches payments on.
      said.push(`The copy the home receives quotes a different reference, so a bank transfer for it may not mention ${inv.invoiceNumber}.`);
    }
  }
  if (r.hadUndatedRuns) {
    // A property of the frozen document that its own lines cannot show.
    said.push('Some of this home\'s runs have no date, so they are not on this invoice.');
  }
  if (r.linesTruncated) {
    // ⚠️ FALSE ON EVERY ROW THE SERVICE WRITES TODAY, and read anyway for the
    // reason `issueAccountInvoice` states where it sets the column: the
    // truncation REFUSAL is what makes it false, so a relaxation of that refusal
    // must arrive on the screen rather than be silently absorbed here.
    said.push('This invoice carries only part of that month.');
  }
  return said.join(' ');
}

/** What an invoice's status means in Helen's words. ⛔ An unknown value falls
 *  back to itself rather than being hidden: a row this build does not understand
 *  still has to be visible, which is the rule the nurse-event list applies. */
const STATUS_WORDS: Record<string, string> = {
  issued: 'Waiting to be paid',
  paid: 'Paid',
  void: 'Cancelled',
};

/** ⭐ board 2764 M7 - a nil invoice's state. It asks for nothing and Stripe marks
 *  it settled at EUR 0 the moment it is finalised, so "Waiting to be paid" and
 *  Stripe's own payment words would both read as money owed (#21). */
export const NIL_STATE_WORDS = 'Nil, nothing to pay';

/**
 * ⭐ board 2764 RW1 (R2) - AN ISSUED INVOICE THAT NEVER REACHED STRIPE, in Helen's
 * words. It used to read "Waiting to be paid", which is false: nobody has been
 * asked for the money, and a failed month-end send looked exactly like a sent
 * one after a reload (Q234). ⛔ The fact is the SERVER's (`atStripe`, the rollup's
 * `stripe_invoice_id IS NOT NULL`), never decided here. A nil one says so too:
 * it has nothing due, and it still has to reach the home as "1 of 2".
 */
export const NOT_SENT_WORDS = 'Not sent yet';
export const NIL_NOT_SENT_WORDS = 'Nil, not sent yet';

/** Issued, and never sent through Stripe (R2). Void and paid rows are neither. */
function isUnsent(inv: ApiPayerInvoice): boolean {
  return inv.status === 'issued' && !inv.atStripe;
}

/** What an outstanding row's state cell says. */
function stateWords(inv: ApiPayerInvoice): string {
  if (isUnsent(inv)) return inv.isNil ? NIL_NOT_SENT_WORDS : NOT_SENT_WORDS;
  if (inv.isNil && inv.status !== 'void') return NIL_STATE_WORDS;
  return STATUS_WORDS[inv.status] ?? inv.status;
}

/** The invoices of the pair, in their number order (DD-3: HSE first). */
const KINDS: readonly ApiInvoiceKind[] = ['hse', 'house'];

function kindLabel(k: ApiInvoiceKind): string {
  return PAYER_WORDS[k] ?? k;
}

function runsWord(n: number): string {
  return n === 1 ? '1 run' : `${String(n)} runs`;
}

/** The segment an outstanding row is labelled with, or `null` for a pre-v44
 *  `'combined'` invoice (one document covering every kind, so no segment). */
function segmentOf(inv: ApiPayerInvoice): string | null {
  return inv.payerKind === 'hse' || inv.payerKind === 'house' ? kindLabel(inv.payerKind) : null;
}

/**
 * ⭐⭐ board 2812 fix round 2 (ux L4, Captain's item 7) - RAISED IS NOT SENT. A card said
 * "This month has already been invoiced" and "already raised" beside the owed table's
 * "Not sent yet", and "invoiced" reads as SENT to Helen. A live kind's invoice is now
 * said as what it is: raised and not sent yet, raised and sent, or raised and paid,
 * read from the owed list's own facts for that payer, month and kind (`atStripe`,
 * `status`, the same facts the owed table's row words come from). When the card's
 * invoice cannot be found there (a payer named differently on its invoice), the card
 * says only "raised", which is true either way.
 */
type RaisedState = 'not-sent' | 'sent' | 'paid' | 'unknown';
const RAISED_WORDS: Readonly<Record<RaisedState, string>> = {
  'not-sent': 'raised, not sent yet',
  sent: 'raised and sent',
  paid: 'raised and paid',
  unknown: 'raised',
};
function raisedStateOf(
  row: ApiAssemblyRow,
  k: ApiInvoiceKind,
  owed: readonly ApiOutstandingRow[],
  year: number,
  month: number,
): RaisedState {
  const found = owed
    .filter((o) => o.payerName === row.payerName)
    .flatMap((o) => o.invoices)
    .filter((i) => i.year === year && i.month === month && i.status !== 'void' && (i.payerKind === k || i.payerKind === 'combined'));
  if (found.length !== 1) return 'unknown';
  const inv = found[0]!;
  if (inv.status === 'paid') return 'paid';
  return inv.atStripe ? 'sent' : 'not-sent';
}

/** ⭐ board 2812 fix round 2 (ux L4): the card's sentence once nothing is left to issue,
 *  from the same states as its lines. One invoice or two, said in the number it is. */
function raisedSentence(states: readonly RaisedState[]): string {
  const noun = states.length === 1 ? 'invoice is' : 'invoices are';
  if (states.length > 0 && states.every((x) => x === 'not-sent')) return `. This month's ${noun} raised and not sent yet.`;
  if (states.length > 0 && states.every((x) => x === 'sent' || x === 'paid')) return `. This month's ${noun} raised and sent.`;
  return `. This month's ${noun} raised.`;
}

/**
 * ⭐ board 2764 M7 (R-8) - ONE LINE PER INVOICE OF THE PAIR, from the roll-up's
 * own per-kind counts. A kind with runs says how many and what they come to; the
 * HSE kind of a month with only house runs is the NIL invoice (RULED D3) and says
 * so; a kind with no runs and no invoice says nothing. A kind whose invoice is live
 * says it is raised, and (board 2812 fix round 2) whether it is sent (`RAISED_WORDS`).
 */
function kindLines(row: ApiAssemblyRow, stateOf: (k: ApiInvoiceKind) => RaisedState): string[] {
  const lines: string[] = [];
  for (const k of KINDS) {
    const r = row.kinds[k];
    const raised = r.live ? `, ${RAISED_WORDS[stateOf(k)]}` : '';
    if (r.runCount > 0) lines.push(`${kindLabel(k)}: ${runsWord(r.runCount)}, ${euro(r.cents)}${raised}`);
    else if (k === 'hse' && row.kinds.house.runCount > 0) lines.push(`${kindLabel(k)}: no trips, nil invoice${raised}`);
  }
  return lines;
}

/**
 * ⭐ board 2764 M4 L4 / M7 - THE RUNS THE ISSUE WOULD NOT BILL, SAID OUT LOUD.
 * A run of a kind whose invoice is live is left off by the issue (it never
 * edits a sent invoice); the remedy is to withdraw that invoice and issue again.
 * A run of a kind with no live invoice (a late house run after an HSE-only month,
 * or a kind whose invoice was withdrawn) is billed by Issue on its own invoice.
 */
function lateLines(row: ApiAssemblyRow): string[] {
  const lines: string[] = [];
  for (const k of KINDS) {
    const r = row.kinds[k];
    if (r.lateCount === 0) continue;
    const them = r.lateCount === 1 ? 'it' : 'them';
    lines.push(r.live
      ? `${kindLabel(k)}: ${runsWord(r.lateCount)} not on this month's ${kindLabel(k)} invoice. To bill ${them}, withdraw that invoice and issue again.`
      : `${kindLabel(k)}: ${runsWord(r.lateCount)} not on an invoice yet. Issue bills ${them} on their own invoice.`);
  }
  return lines;
}

/** Nothing is left for a press to do: no kind to issue and no run waiting for a
 *  payer. The server's answer (`kindsToIssue`), never `alreadyInvoiced`. */
function nothingLeft(row: ApiAssemblyRow): boolean {
  return row.kindsToIssue.length === 0 && row.noPayerKindCount === 0;
}

/** board 2764 RW1 (R1): the payer cannot be reached, so Issue is refused
 *  (`no_billing_email`). `=== false`: an absent field is not a refusal. */
function lacksBillingEmail(row: ApiAssemblyRow): boolean {
  return row.hasBillingEmail === false;
}

/** Whether the card offers Issue: the server says a press would create an
 *  invoice, or runs with no payer set are waiting to be named by the refusal
 *  (R-5 is checked before the kinds, so a press is how Helen reaches them).
 *  ⭐ board 2764 RW1 (R1): never for a payer with no billing email. The issue
 *  refuses it before a number is taken, so a button here would be a control
 *  whose only outcome is a refusal (P0-6); the card says why instead. */
function offersIssue(row: ApiAssemblyRow): boolean {
  return !lacksBillingEmail(row) && !nothingLeft(row) && !namesUnavailable(row);
}

/**
 * ⭐ board 3059 (D4) - this payer's resident-names switch is ON and the names could
 * not be read, so the card offers no Issue: the route would refuse it
 * (`resident_names_unavailable`) before taking a number. A control whose only
 * outcome is a refusal is the P0-6 defect, so the card says why instead.
 */
function namesUnavailable(row: ApiAssemblyRow): boolean {
  return row.residentNames === 'unavailable';
}

/** board 3059 - the card's line when the names could not be read. */
export const NAMES_UNAVAILABLE_WORDS =
  'Resident names are on for this payer, and they could not be read just now, so Issue is not offered. Reload the screen in a moment.';

/**
 * ⭐ board 3059 (D5) - WHAT AN ON PAYER'S INVOICE LINE WILL SAY ABOUT A RUN, in the
 * preview, so Helen sees each case before she issues: the name the line prints,
 * or "resident not recorded" and why. The words in brackets are the preview's
 * only; the line itself says "resident not recorded".
 */
export function residentLabelWords(label: ApiResidentLabel): string {
  if (label.kind === 'named') return label.name;
  const why = label.reason === 'no-portal-request'
    ? 'no portal request'
    : label.reason === 'account-changed'
      // board 3059 fix round 1 (security M1): the run moved to another account
      // after the request crossed, so its resident is not this payer's to name.
      ? 'moved from another account since the request'
      : label.reason === 'ref-changed'
        ? 'reference changed since the request'
        : 'not found in the portal';
  return `resident not recorded (${why})`;
}

/**
 * ⭐ board 2764 RW1 (R1) - the card's sentence for a payer with no billing
 * email, said BEFORE any press.
 * ⭐ RW1 round 2 (ux finding 2): it says what IS so (no email is set, so nothing
 * can be sent YET), never that something failed, and it says WHERE, in the words
 * on screen: ⭐ board 2812 M4 fix round (L7) the home's page in the column of homes
 * and its **Details** button (`HOME_PAGE_WORDS.details`), or **Set up homes and
 * places** (`HOME_PAGE_WORDS.setUp`) then **Accounts** (`SECTION_LABELS.accounts`
 * in NursingHomes.tsx), the account's **Edit**, and the form's own **Billing
 * email** label (AccountList.tsx). The Homes tab it used to name is gone. `home` is the
 * roll-up's anchor site, named from the accounts book the parent already read,
 * never an email; a payer billing several homes stores ONE email on the payer,
 * so any of its homes' forms sets it.
 */
export function noBillingEmailWords(home: string, siteCount: number): string {
  const where = siteCount > 1 ? `${home} or any other home this payer bills` : home;
  return `No billing email is set for this payer yet, so its invoices cannot be sent. `
    + `Open ${where} in the column of homes and press Details, or open Set up homes and places, then Accounts. `
    + 'Press Edit, fill in Billing email and save. Then you can issue.';
}

/**
 * The confirm-in-place question and what the press does. ⛔ THE COUNT IS THE
 * SERVER'S (`kindsToIssue`), and with runs that have no payer set it is not
 * knowable until each has one, so the question then names no count.
 */
function issueConfirmWords(row: ApiAssemblyRow, period: string): string {
  const n = row.kindsToIssue.length;
  if (row.noPayerKindCount > 0 || n === 0) {
    return `Issue the invoices for ${period}? Any run with no payer set is listed for you first, and nothing is issued until each has one. `
      + 'Once raised, an invoice can only be withdrawn, and its runs can no longer be cancelled.';
  }
  const cents = row.kindsToIssue.reduce((sum, k) => sum + row.kinds[k].cents, 0);
  return n === 1
    ? `Issue 1 invoice for ${period}? It goes to the home straight away, for ${euro(cents)}. `
      + 'Once raised, it can only be withdrawn, and its runs can no longer be cancelled.'
    : `Issue ${String(n)} invoices for ${period}? Both go to the home straight away, ${euro(cents)} in all. `
      + 'Once raised, an invoice can only be withdrawn, and its runs can no longer be cancelled.';
}

/**
 * ⭐ WHAT EACH REFUSAL MEANS AND WHAT TO DO ABOUT IT.
 *
 * ⛔ EVERY REASON IS ENUMERATED, NOT DEFAULTED. Behaviour by exclusion swallows
 * what it did not enumerate, and each of these means a live contract is billing
 * ONE run a month instead of four — the exact under-bill the generator exists to
 * close, still open, on a contract nobody has been told about. A reason this build
 * does not know still renders, with its own words, at the foot.
 */
/**
 * ⛔⛔ T1719 M5 STEP 0(b) — EXPORTED SO A SPEC CAN ASSERT THE **EVALUATED**
 * SENTENCES, NOT THE SOURCE TEXT.
 *
 * The docblock above says ASCII only and, until M5, NOTHING enforced it: the
 * `reference-unmintable` entry carried a `\u2019` curly apostrophe written as a
 * JS escape, so the SOURCE LINE was already pure ASCII and a text-level census
 * (`job-ref-parity.spec.ts` reads these files as text) was green before the fix
 * and after it. `check-copy.sh:191` records the same blind spot deliberately: a
 * backslash-u escape is not banned, and the kiosk-client region bans the em dash
 * only, so `check:copy` cannot see this either way.
 * ⇒ the only instrument that can grade it is one that reads the RUNTIME strings,
 * which is `account-invoices-tab.spec.tsx`'s ASCII arm over
 * `Object.values(SHORTFALL_WORDS)` — the idiom `account-actions.spec.ts:157-160`
 * already uses on the evaluated 409 sentences.
 * ⛔ EXPORTED FOR THAT ARM AND FOR NOTHING ELSE. It is not a second copy table:
 * the screen below is the only renderer.
 */
export const SHORTFALL_WORDS: Record<string, string> = {
  /* ⚠️ THE WORDS TRACK THE WINDOW THE SERVER MEASURES, which is from TODAY to the
     end of the month and NOT the whole month (see `diagnoseSeriesShortfalls`).
     "this month" here would name days the diary is never allowed to fill, and the
     remedy this sentence offers would not clear the card. */
  'missing-runs':
    'The diary holds fewer runs between today and the end of the month than this contract says it should. Use Bring the diary up to date, then check again.',
  'duplicate-pattern':
    'Two contracts here are set up identically, so neither is putting runs in the diary. Stop the one that was entered twice.',
  'unrecognised-schedule':
    'The days or the frequency on this contract are not ones the diary understands, so it is putting no runs in at all. Re-enter the schedule on the booking.',
  'off-pattern-anchor':
    'This contract runs monthly on a date the pattern cannot describe, so nothing is being put in the diary. Re-enter it as a weekly or fortnightly contract.',
  'no-dated-anchor':
    'This contract has no dated booking behind it, so there is nothing to copy a run from. Give the original booking a date.',
  /* ⛔⛔ T1719 M4d (review batch D) — THE REMEDY HERE IS A DIFFERENT SCREEN, AND
     SAYING SO IS THE WHOLE POINT. Before this entry a skipped contract rendered
     as `missing-runs`, whose words are "Use Bring the diary up to date, then
     check again" — and pressing that skips the same contract again, for ever.
     `generate-occurrences.ts` forbids exactly that: a warning naming a remedy
     that cannot clear it. ⚠️ It names the SHORT CODE box rather than the code
     itself: a code identifies the home, and this card is already headed by the
     home's name. ASCII only. */
  'reference-unmintable':
    'This home\'s short code cannot make a job reference, so the diary is putting no runs in for it at all. Fix the short code on the Accounts screen, then use Bring the diary up to date.',
  /* ⭐ board 2814 M4 fix round 1: a contract holding a cash run never repeats it.
     The remedy is the booking, not this screen. ASCII only. */
  'cash-anchor':
    'This contract is for a trip paid in cash to the driver, so it cannot repeat and the diary puts no runs in for it. Book each trip on its own.',
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: ApiBillingRollUp };

/**
 * An issued document the SERVER reported as not sent, carried on the notice that
 * reported it so the remedy sits beside the words. ⛔ Its presence is what offers
 * the send, and it comes from `sentToStripe: false` on the route's own response -
 * this screen never decides for itself whether something has been sent.
 */
type UnsentInvoice = { accountInvoiceId: number; invoiceNumber: string; isNil: boolean };

/**
 * ⭐ board 2764 M7 - `unsent` IS A LIST: an issue now creates up to two invoices
 * and each is sent best effort, so either, both or neither can be unsent.
 * ⛔ M5 security L4: a NIL invoice is offered a send ONLY here, where the server
 * has just said it never went out (its send stopped before Stripe, or part way
 * through, and the re-send route resumes it). A nil invoice that WAS sent is
 * never offered one: a re-send of a paid EUR 0 invoice emails the home again
 * after Stripe's idempotency window, and its outstanding row reads
 * `NIL_STATE_WORDS` with no send control at all.
 */
type Notice = { kind: 'ok' | 'error'; text: string; unsent?: UnsentInvoice[] };

/**
 * ⭐ board 2764 M7 (R-5 UI) - the runs a `payer_kind_missing` refusal named, held
 * under the card that was pressed. `chosen` records a payer set from here, so the
 * row says what was saved instead of offering the buttons again.
 */
type PayerRefusal = {
  payerId: string;
  runs: Array<ApiRunWithoutPayer & { chosen?: ApiInvoiceKind }>;
  total: number;
};

/** At most this many runs, each with two buttons, so the refusal adds at most 20
 *  controls to a card ("too many buttons" is the history, #21). */
const REFUSAL_RUNS_SHOWN = 10;

/** ⭐ board 2812 M5 step 0 (f): what is owed, in All homes' words and in a home's own
 *  (M4's ux-review N4: a home's Invoices read "added up per home"). R-11: for Vicky. */
/* ⭐⭐ board 2812 fix round (Captain's /check item 7): THE LINE UNDER THE HEADING IS TRUE
   FOR THE STATE IT IS SHOWN IN. It read "Every invoice that has gone out ... and has not
   been paid" above a list that holds EVERY invoice of the home (sent, not sent, paid and
   withdrawn: `listOutstandingByPayer`), beside "2 not sent yet". Owed adds up the sent,
   unpaid ones only (`outstandingCents`), so the line says what Owed counts when there is
   something sent and unpaid (`owed`), says nothing is owed when there is not
   (`nothingOwed`), and is not shown at all when there are no invoices (`none` says it). */
export const OWE_WORDS = {
  allHomes: {
    loading: 'Getting what every home owes…',
    unread: 'Could not read what the homes owe, so nothing is shown here. Nothing has been deleted.',
    heading: 'What the homes owe',
    owed: 'Owed is what each home has been sent and not paid yet.',
    nothingOwed: 'Nothing is owed: no invoice sent to a home is waiting to be paid.',
    none: 'No invoices have been raised yet, so nothing is owed.',
  },
  oneHome: {
    loading: 'Getting what this home owes…',
    unread: 'Could not read what this home owes, so nothing is shown here. Nothing has been deleted.',
    heading: 'What this home owes',
    owed: 'Owed is what this home has been sent and not paid yet.',
    nothingOwed: 'Nothing is owed: no invoice sent to this home is waiting to be paid.',
    none: 'No invoices have been raised for this home yet, so nothing is owed.',
  },
} as const;

/** ⭐ board 2812 fix round (item 7): an invoice Owed counts. The server's own membership
 *  (`issued` AND at Stripe), less a nil invoice, which asks for nothing. */
function owedOn(inv: ApiPayerInvoice): boolean {
  return inv.status === 'issued' && inv.atStripe && !inv.isNil;
}

/**
 * ⭐⭐ board 2812 fix round (item 7): THE QUEUE'S LINE, FROM THE CARDS' OWN FACTS. It said
 * every run listed was "not on an invoice yet" above a card reading "This month has
 * already been invoiced" and "already raised". A run is on an invoice when its kind's
 * invoice is live (`kinds[k].live`) and it is not a late run; the card says which.
 * With no cards, the empty state speaks and this says nothing.
 */
function queueStateLine(rows: readonly ApiAssemblyRow[], period: string, oneHome: boolean): string | null {
  if (rows.length === 0) return null;
  const raised = (row: ApiAssemblyRow) => row.alreadyInvoiced || KINDS.some((k) => row.kinds[k].live);
  const allOn = (row: ApiAssemblyRow) => row.alreadyInvoiced && nothingLeft(row) && KINDS.every((k) => row.kinds[k].lateCount === 0);
  if (!rows.some(raised)) {
    return `These are the runs that travelled in ${period}. None of them is on an invoice yet, and nothing is billed until an invoice is raised.`;
  }
  if (rows.every(allOn)) {
    return oneHome
      ? `These are the runs that travelled in ${period}, and every one of them is on an invoice raised for this home.`
      : `These are the runs that travelled in ${period}, and every one of them is on a raised invoice.`;
  }
  return `These are the runs that travelled in ${period}. Some are on a raised invoice, as their lines say. The rest are billed only when an invoice is raised.`;
}

export function AccountInvoices({ accounts, accountId, onStale }: AccountInvoicesProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The 'YYYY-MM' this pane is showing, or `null` before the first read has told
   * us what the server's own month is. ⛔ HELD, so every reload after an action
   * asks for the SAME month rather than falling back to the server's default and
   * silently moving the queue under the operator mid-task.
   */
  const [askedMonth, setAskedMonth] = useState<string | null>(null);
  /** Which payer's invoice list is open, by opaque payer id. One at a time: the
   *  resting pane is a list to scan, not N tables to skip past. */
  const [openPayer, setOpenPayer] = useState<string | null>(null);
  /** Which assembly card's detail line is open. */
  const [openQueue, setOpenQueue] = useState<string | null>(null);
  /**
   * Which assembly card is showing its "yes, issue it" confirm, by opaque payer
   * id. ⛔ ONE AT A TIME AND HELD HERE, like every other per-row disclosure on
   * this pane, so a `load()` cannot leave two cards half-open.
   */
  const [confirmIssue, setConfirmIssue] = useState<string | null>(null);
  /** ⭐ board 2764 M7 - the R-5 refusal's runs, under its card. One at a time. */
  const [refusal, setRefusal] = useState<PayerRefusal | null>(null);
  /**
   * ⭐ board 2764 RW1 (R3) - what the last payer-kind correction said, under the
   * runs list of ITS card. ⛔ Under the list, never under the run: a run the route
   * refused as `invoiced` leaves the list on the reload that follows, and a note
   * keyed to its row would vanish with it.
   */
  const [correction, setCorrection] = useState<{ payerId: string; text: string } | null>(null);
  /**
   * ⭐ board 2764 M6 - what the last PDF press said, under ITS row only. `null` =
   * nothing to say (the download started, or nothing was pressed). ⛔ ONE AT A
   * TIME, like every other per-row disclosure on this pane.
   */
  const [pdfNote, setPdfNote] = useState<{ id: number; text: string } | null>(null);
  /**
   * ⭐ T1447 M6 — the recovery panel. ⛔ ONE AT A TIME AND ONE STATE FOR ALL
   * THREE, like every other per-row disclosure on this pane: a `load()` after an
   * action cannot leave two panels half-open, and an operator cannot have a
   * withdrawal confirm open on one document while typing an amount into another.
   */
  const [openAction, setOpenAction] = useState<{ id: number; kind: 'void' | 'paid' | 'stripe' } | null>(null);
  /** ⛔ THE OPERATOR'S TYPED FIGURE, HELD AS THE STRING SHE TYPED. Never parsed
   *  into state and re-rendered: re-formatting a half-typed amount under the
   *  person typing it is how "12.5" becomes "12.50" mid-keystroke and then
   *  "1250". `centsFromTypedEuro` runs once, at the press. */
  const [paidAmount, setPaidAmount] = useState('');
  /** What Stripe said. `undefined` = not asked, `null` = the read FAILED. ⛔
   *  Never an empty object for a failure — "Stripe holds nothing" and "we could
   *  not ask" are different facts with different remedies. */
  const [stripeState, setStripeState] = useState<ApiInvoiceAtStripe | null | undefined>(undefined);

  /**
   * ⭐ READ THE MONTH THIS PANE INVOICES - and that is the LAST COMPLETED month,
   * never the current one.
   *
   * ⛔⛔ TWO READS ON FIRST OPEN, DELIBERATELY, AND THE FIRST ONE IS THE CLOCK.
   * `issueAccountInvoice` refuses a period that has not ended, so a queue showing
   * the current month can never be invoiced; and the month must NOT be computed
   * from `new Date()`, because then one button on two machines in different
   * offsets would assemble two different months on the 1st. The route resolves
   * an absent `month` in Europe/Dublin and sends the window home, so the first
   * read asks for nothing and is used only for the month it names, and the second
   * asks for the month before it. Every read after that passes the held key, so
   * the cost is one extra read per open of this tab.
   *
   * ⚠️ THE FIRST RESPONSE IS DISCARDED RATHER THAN RENDERED. Painting it would
   * show the current month's queue for a tick, on a pane whose every card carries
   * money and a period, and the operator would watch the figures change under
   * them. The loading state already says the honest thing.
   */
  function load(month: string | null): void {
    void fetchBillingRollUp(month ?? undefined, accountId).then(
      (data) => {
        if (month === null) {
          // The server has just told us its own month. Ask for the one before it,
          // which is the last one that can have ended. ⛔ Bounded to exactly two
          // reads: this arm is unreachable from the second call, which passes a key.
          const wanted = monthBefore(data.year, data.month);
          const key = monthKeyOf(wanted.year, wanted.month);
          setAskedMonth(key);
          load(key);
          return;
        }
        setState({ kind: 'ready', data });
      },
      (e: unknown) => {
        // ⭐ board 2812 M4 fix round (C2): a home's Invoices for an account the server no
        // longer knows is not a page; the host opens All homes.
        if (accountId !== undefined && e instanceof ApiError && e.status === 404 && onStale !== undefined) {
          onStale();
          return;
        }
        setState({ kind: 'error' });
      },
    );
  }
  useEffect(() => { load(null); }, []);

  const accountName = (id: number): string | null =>
    (accounts ?? []).find((a) => a.accountId === id)?.name ?? null;
  /** ⭐ board 2812 M5 step 0 (f): on a home's page (`accountId`) what is owed is THAT
   *  home's, and the words say so; All homes' Invoices keeps "the homes". */
  const owe = accountId === undefined ? OWE_WORDS.allHomes : OWE_WORDS.oneHome;
  /** ⭐ board 2812 M5 (fix round 1, U4): a home's page names its home in its title, so
   *  its owed table drops the Home column (one row, always that home); All homes keeps
   *  it. The open row's cell spans whichever columns are drawn. */
  const showHomeColumn = accountId === undefined;

  async function onTopUp(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await topUpOccurrences();
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        return;
      }
      const r = result.value;
      // ⛔⛔ WITHDRAWALS ARE REPORTED, AND THE "NOTHING HAPPENED" ARM COUNTS BOTH
      // NUMBERS (ux-review B2). This one control does not only ADD runs: the same
      // pass moves every generated run the contract no longer covers to
      // 'cancelled' (`generateOccurrences` -> `setBookingStatus(...,'cancelled')`,
      // reported as `withdrawn`). Rendering `created` alone meant a press that
      // withdrew five and created none said "The diary was already up to date.
      // Nothing new was added" — false, five runs had just been cancelled — and a
      // press that created three and withdrew five never mentioned the five at
      // all. A cancelled run is a run that will not be billed, announced on the
      // screen whose whole purpose is what is owed, under a label that reads as
      // purely additive.
      // ⛔ THE NUMBERS ARE REPORTED EVEN WHEN BOTH ARE ZERO, and that is the
      // ordinary answer: capture already puts a contract's runs in the diary, so
      // this usually has nothing to do. Saying "done" with no figure would make a
      // no-op indistinguishable from a fix.
      const said: string[] = [];
      if (r.created > 0) said.push(`${String(r.created)} run(s) added to the diary`);
      if (r.withdrawn > 0) {
        said.push(
          `${String(r.withdrawn)} run(s) taken out of the diary, because the contract behind them no longer covers those dates`,
        );
      }
      /**
       * ⛔⛔ T1719 M4c — A SKIPPED HOME IS SAID OUT LOUD, AND THE "NOTHING
       * HAPPENED" ARM MUST NOT SWALLOW IT.
       *
       * Since M4b one contract whose account cannot produce a valid job
       * reference is SKIPPED rather than aborting the whole run — which is the
       * fix — but the route's `unmintable` list had no reader here. MEASURED on
       * the D1 fixture: `created 0, withdrawn 0`, and this notice said "The
       * diary was already up to date. Nothing new was added" while a home had
       * just been skipped. Pushing into `said` is what closes that: the
       * no-op sentence is unreachable whenever anything was skipped.
       *
       * ⛔ NO NEW COMPONENT AND NO BADGE — the screen's hard constraint is
       * simplicity, and the WHICH-HOME answer already exists twelve lines down:
       * the shortfall card resolves the account name and states the remedy. A
       * second surface for the same fact is how two of them come to disagree.
       * ⚠️ "contract(s)", not "series": `SHORTFALL_WORDS` and every other
       * sentence on this screen call it a contract, and one screen should use
       * one word for one thing.
       * ⚠️ `?? []` because `postJson` casts the body unchecked — a server that
       * predates the field would otherwise throw inside this click handler.
       */
      const unmintable = r.unmintable ?? [];
      if (unmintable.length > 0) {
        said.push(
          `${String(unmintable.length)} contract(s) could not be given a job reference, so no runs were added for them. The card below names the home and what to fix`,
        );
      }
      setNotice({
        kind: 'ok',
        text: said.length === 0
          ? `The diary was already up to date. Nothing new was added, up to ${r.untilExclusive}.`
          : `${said.join('. ')}. Up to ${r.untilExclusive}.`,
      });
      load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ RAISE ONE PAYER'S INVOICE FOR THE MONTH THIS PANE IS SHOWING - the
   * milestone's deliverable, and the thing this screen shipped without.
   *
   * ⛔ THE VIEW IS RELOADED, NEVER PATCHED BY HAND. Issuing changes two things
   * the screen renders from the server: the card's `alreadyInvoiced` flips, and
   * the outstanding table gains a document. Both are derived server-side (the
   * ledger decides what is outstanding, not this component), so a local edit
   * would be this screen's own guess at what the database now holds.
   */
  async function onIssue(row: ApiAssemblyRow, monthKey: string, period: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setRefusal(null);
    try {
      // ⚠️ THE ANCHOR SITE, AND THE DOCUMENT IS THE PAYER'S. The route resolves
      // the payer from whichever account it is given; this row IS a payer, and
      // `anchorAccountId` is the stable site it was grouped under.
      // ⭐ board 3059 (D3): Issue says whether this card showed resident names, and
      // the route refuses if the payer's switch has changed since.
      const result = await issueAccountInvoiceForMonth(row.anchorAccountId, monthKey, row.residentNames !== undefined);
      if (!result.ok) {
        // ⭐ board 2764 M7 (R-5): the runs to set go under THIS card, with the two
        // payer buttons beside each; the words are the client's own.
        if (result.missingPayer !== null) {
          setRefusal({ payerId: row.payerId, runs: result.missingPayer.runs, total: result.missingPayer.total });
        } else {
          setNotice({ kind: 'error', text: result.message });
        }
        return;
      }
      // ⛔ THE REMEDY RIDES WITH THE REPORT, and only where the SERVER said the
      // document is unsent. An invoice that went out carries no offer, so the
      // pane never shows a control whose only outcome is a refusal.
      const unsent = result.value.invoices
        .filter((inv) => !inv.sentToStripe)
        .map((inv) => ({ accountInvoiceId: inv.accountInvoiceId, invoiceNumber: inv.invoiceNumber, isNil: inv.isNil }));
      setNotice({
        kind: 'ok',
        text: issuedWords(result.value, row.payerName, period),
        ...(unsent.length === 0 ? {} : { unsent }),
      });
      load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ board 2764 M7 (R-5 UI) - SET WHO PAYS FOR ONE RUN THE REFUSAL NAMED, through
   * the M3 route. ⛔ The view is RELOADED, so the card's "no payer set" count is
   * the server's; the refusal row only records what was saved, and Issue is
   * pressed again by Helen, never chained here.
   */
  async function onSetPayer(run: ApiRunWithoutPayer, kind: ApiInvoiceKind): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await setBookingPayerKind(run.bookingId, kind);
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message });
        return;
      }
      setRefusal((prev) => prev && {
        ...prev,
        runs: prev.runs.map((r) => (r.bookingId === run.bookingId ? { ...r, chosen: kind } : r)),
      });
      load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ FINISH THE JOB ON A DOCUMENT THAT ISSUED AND NEVER WENT OUT.
   *
   * ⛔ IT IS THE ONLY WAY BACK. The number is spent and the runs are frozen the
   * moment the ledger row commits, so pressing Issue again answers 409 off the
   * payer-period index while `stripe_invoice_id` is still NULL. Before this,
   * "issued but not sent" was a state the screen REPORTED and could not clear.
   *
   * ⛔ THE OFFER SURVIVES A FAILURE, AND THAT IS DELIBERATE. Five of the route's
   * refusals share status 409 with no machine-readable `code` (board 3059's
   * `resident_names_unavailable` is a sixth, and carries one), so the client
   * cannot tell "already sent" from "that payer has no billing email" and must
   * not guess: dropping the control on any refusal would silently retire the only
   * remedy for the retryable ones (a Stripe outage is the likeliest cause of the
   * state this control exists for). A second press cannot mint a second
   * invoice - the route refuses while `stripe_invoice_id IS NOT NULL`, which is
   * where that guarantee belongs. ⚠️ board 2764 M5: EXCEPT a NIL (nothing due)
   * invoice, which the route RESUMES instead: a EUR 0 one already sent is
   * EMAILED AGAIN on each press (to the payer's current billing email only;
   * a mismatch is refused). It asks nobody for money. ⛔ board 2764 M7 (M5
   * security L4): so it is offered for a nil invoice ONLY to finish one the
   * issue reported unsent, never for one that went out (see `Notice`).
   *
   * ⭐ board 2764 RW1 (R2) - AND FROM THE INVOICE'S OWN ROW, which survives a
   * reload. It used to be reachable only from the notice that reported the state,
   * on the ground that nothing on the wire said whether an outstanding invoice was
   * ever sent. That stopped being true at T1447 M6: `atStripe` (the rollup's
   * `stripe_invoice_id IS NOT NULL`) is on every outstanding row, and an `issued`
   * row without it is exactly the state this clears. A nil row offers it only on
   * that same fact (never once Stripe holds it, M5 security L4).
   */
  async function onResend(unsent: UnsentInvoice, offered: UnsentInvoice[]): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      const result = await resendAccountInvoice(unsent.accountInvoiceId);
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message, unsent: offered });
        return;
      }
      const said = [unsent.isNil
        ? `Invoice ${unsent.invoiceNumber} has now been sent to the home. It asks for nothing.`
        : `Invoice ${unsent.invoiceNumber} has now been sent to the home for payment.`];
      if (!result.value.ourNumberIsTheReference) {
        said.push(`The copy the home receives quotes a different reference, so a bank transfer for it may not mention ${unsent.invoiceNumber}.`);
      }
      // ⛔ THIS ONE'S OFFER GOES ON THE WAY OUT: the document has gone. This is
      // the one state the screen can be sure about. Any OTHER invoice of the
      // pair the server reported unsent keeps its offer.
      const rest = offered.filter((u) => u.accountInvoiceId !== unsent.accountInvoiceId);
      setNotice({ kind: 'ok', text: said.join(' '), ...(rest.length === 0 ? {} : { unsent: rest }) });
      load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ board 2764 RW1 (R3) - CORRECT WHO PAYS FOR ONE RUN that no live invoice
   * carries, from the card's "What is in this" list, through the M3 route. ⛔ The
   * route refuses a run on a live invoice (409 `invoiced`) and that refusal is
   * worded by `setBookingPayerKind`. The view is RELOADED either way, so the
   * card's per-kind lines and the list are the server's, never a local guess.
   */
  async function onCorrectPayer(payerId: string, run: ApiUninvoicedRun, kind: ApiInvoiceKind): Promise<void> {
    if (busy) return;
    setBusy(true);
    setCorrection(null);
    try {
      const result = await setBookingPayerKind(run.bookingId, kind);
      const which = `${run.jobRef ?? `booking ${String(run.bookingId)}`} on ${civilDateLabel(run.journeyDate, null)}`;
      setCorrection({
        payerId,
        text: result.ok ? `${kindLabel(kind)} now pays for ${which}.` : `${which}: ${result.message}`,
      });
      load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  async function onStopSeries(seriesId: number, name: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await setBookingSeriesActive(seriesId, false);
      setNotice(result.ok
        ? { kind: 'ok', text: `That contract for ${name} is stopped. It will put no more runs in the diary.` }
        : { kind: 'error', text: result.message });
      if (result.ok) load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ board 2764 M6 (R-12, RULED D4) - open Stripe's PDF of one invoice. ⛔ A
   * READ: it changes nothing here or at Stripe, so it takes no `busy` slot.
   * ⛔ THE FIRST HOUR IS A STATE, NOT A FAILURE: a non-nil invoice is a Stripe
   * draft until it finalises, and the route answers 409 `not_ready`, which this
   * pane words under the row instead of downloading an error body (L9).
   */
  async function onOpenPdf(inv: ApiPayerInvoice): Promise<void> {
    setPdfNote(null);
    const result = await downloadAccountInvoicePdf(inv.accountInvoiceId, inv.invoiceNumber);
    if (!result.ok) setPdfNote({ id: inv.accountInvoiceId, text: result.message });
  }

  /** Open or close one recovery panel. ⛔ The typed amount is CLEARED on every
   *  transition: carrying a figure typed against one invoice into the panel of
   *  another is exactly the misattribution the exact comparator cannot catch. */
  function onToggleAction(id: number, kind: 'void' | 'paid' | 'stripe'): void {
    setPaidAmount('');
    setStripeState(undefined);
    if (openAction?.id === id && openAction.kind === kind) {
      setOpenAction(null);
      return;
    }
    setOpenAction({ id, kind });
    if (kind === 'stripe') {
      void fetchInvoiceAtStripe(id).then(
        (answer) => { setStripeState(answer); },
        // ⛔ null, NOT a fabricated "nothing at Stripe". A failed read and an
        // invoice Stripe never received are different facts.
        () => { setStripeState(null); },
      );
    }
  }

  /**
   * ⭐ WITHDRAW ONE DOCUMENT — the control THREE live error strings have told
   * Helen to use since T1011 M4, and which did not exist until now.
   *
   * ⛔ THE VIEW IS RELOADED, NEVER PATCHED BY HAND. A withdrawal changes what the
   * server derives: the row's state, the payer's outstanding total, and whether
   * that payer-period can be issued again. All three are the ledger's answer, so
   * a local edit would be this screen's guess at what the database now holds.
   */
  async function onVoid(inv: ApiPayerInvoice): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await voidAccountInvoice(inv.accountInvoiceId);
      setNotice(result.ok
        ? { kind: 'ok', text: withdrawnWords({ invoiceNumber: inv.invoiceNumber, stripeWithdrawal: result.value.stripeWithdrawal }) }
        : { kind: 'error', text: result.message });
      if (result.ok) load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  /**
   * ⭐ RECORD A BANK PAYMENT AGAINST ONE DOCUMENT.
   *
   * ⛔⛔ IT SENDS THE FIGURE HELEN TYPED AND NEVER `inv.cents`, WHICH THIS
   * COMPONENT HAS ONE FIELD AWAY. The server compares the amount to the frozen
   * total EXACTLY, and says why: two homes on identical recurring contracts have
   * identical monthly totals, so an inexact comparator "is how one home's money
   * pays another's bill". Passing the summed total from here would make that
   * comparison a TAUTOLOGY and delete the control outright.
   */
  async function onMarkPaid(inv: ApiPayerInvoice): Promise<void> {
    if (busy) return;
    const cents = centsFromTypedEuro(paidAmount);
    if (cents === null) {
      // ⛔ REFUSED HERE RATHER THAN SENT AS NaN. The route would answer 400 and
      // the copy would be about the server, when the fault is a figure this
      // screen can name precisely.
      setNotice({ kind: 'error', text: 'Nothing was recorded. Type the amount the home paid, in euro and cent, for example 1250.00.' });
      return;
    }
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await markAccountInvoicePaidByOperator(inv.accountInvoiceId, cents);
      setNotice(result.ok
        ? {
          kind: 'ok',
          text: `Invoice ${inv.invoiceNumber} is marked paid, ${euro(result.value.totalCents)} received.`
            + (inv.atStripe
              // ⛔⛔ THE RESIDUAL, IN WORDS, EVERY TIME. A document that went out
              // through Stripe carries auto_advance + send_invoice, so recording
              // a BANK payment here leaves the Stripe copy a LIVE PAYABLE PAGE
              // that Stripe keeps chasing. Nothing in this build closes that —
              // auto-voiding at Stripe on a mark-paid is scope this milestone
              // was not given, and the choice is a commercial one (raised as a
              // numbered question in docs/OPEN-QUESTIONS.md). Saying nothing
              // would be the failure mode.
              ? ' ⚠️ This one also went out through Stripe, and that copy can still be paid and is still being chased. Use Check at Stripe, and tell Alex if it needs stopping.'
              : '')
            + ' The runs on it stay billed to this month.',
        }
        : { kind: 'error', text: result.message });
      if (result.ok) load(askedMonth);
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <div class="op-review">
        <p class="helper-note" role="status">{owe.loading}</p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div class="op-review">
        {/* ⛔ A FAILED READ IS NEVER AN EMPTY LIST. "Nothing is owed" is a
            legitimate success on this pane, so a fallback would be
            indistinguishable from a settled book on the one screen built to say
            what is outstanding. */}
        <Banner kind="error">
          {owe.unread}{' '}
          {/* ⚠️ `() => load(askedMonth)`, NEVER a bare `onClick={load}`: the
              handler now takes the month, so a bare reference would hand it a
              click EVENT and ask the route for a month it must 400 on. Retrying
              before the first read settled passes `null`, which correctly starts
              the two-read sequence over. */}
          <button
            type="button"
            class="btn btn--quiet btn--inline"
            onClick={() => { load(askedMonth); }}
          >
            Try again
          </button>
        </Banner>
      </div>
    );
  }

  const { data } = state;
  const period = periodWords(data.year, data.month);
  /** ⭐ board 2812 fix round (item 7): the queue's line, true for its cards' state. */
  const queueLine = queueStateLine(data.assembly.rows, period, accountId !== undefined);
  /** 'YYYY-MM' for the month on screen - what the issue route is asked for. ⛔ The
   *  SERVER'S resolved window, not `askedMonth`: the route decides what it
   *  answered for, and the two agree only because it says so. */
  const monthKey = monthKeyOf(data.year, data.month);
  const nextMonth = monthAfter(data.year, data.month);
  /** The unsent document the current notice is about, if it has one. Bound here
   *  so the offer below reads a `const` rather than re-narrowing inside a
   *  callback. */
  const unsent = notice?.unsent;
  /**
   * ⛔ WHETHER THE PERIOD CAN BE ISSUED AT ALL, DERIVED THE WAY THE SERVICE
   * DERIVES IT. `issueAccountInvoice` step (0) refuses while
   * `today < monthEndExclusive`, and this is that same half-open comparison over
   * the same two server-supplied civil dates.
   *
   * ⚠️ IT IS TRUE FOR EVERY CARD THIS PANE SHOWS TODAY, AND THE DISABLED ARM IS
   * KEPT ANYWAY. It used to be FALSE for every card - `fetchBillingRollUp` asked
   * for no month, so the queue was always the CURRENT one, which is never over,
   * and the button below could only ever render disabled. The pane now opens on
   * the last COMPLETED month, so the ordinary case is live.
   * ⛔ DO NOT DELETE THE DISABLED ARM ON THE STRENGTH OF THAT. It is now a GUARD
   * rather than the resting state: it is the only thing standing between a
   * regression in the month derivation (or a later month-selection surface) and a
   * screen that invites Helen to press a button the service must refuse. Both
   * sides are pinned in test/client/account-invoices-tab.spec.tsx, from FIXTURES,
   * because production can no longer reach the disabled side by itself.
   * ⛔ AND NEVER RELAX THE REFUSAL INSTEAD. A part-month document spends the
   * payer's ONE live slot for that month and strands the remainder, which is why
   * the service takes no override. The fix is showing an issuable month, never
   * permitting an unissuable one.
   */
  const periodIsOver = data.computedOn >= firstDayIso(nextMonth.year, nextMonth.month);
  const issuableFromWords = `1 ${MONTH_NAMES[nextMonth.month - 1] ?? String(nextMonth.month)} ${String(nextMonth.year)}`;

  return (
    <div class="op-review">
      {notice && (
        <Banner kind={notice.kind}>
          {notice.text}
          {unsent !== undefined && unsent.map((u) => (
            <>
              {' '}
              {/* ⛔⛔ THE REMEDY FOR THE STATE THIS BANNER JUST REPORTED. The
                  issue route never fails over a document that exists, so
                  "issued but not sent" arrives as a 200 - and until now the
                  screen said so and offered nothing. ⛔ It names the invoice
                  rather than the home, so the accessible name carries no
                  identifiable party and needs no `data-fb-opaque`. ⛔ And it is
                  `btn--quiet btn--inline`, the same shape as "Try again" above:
                  this pane keeps at most ONE dominant primary action.
                  ⭐ board 2764 M7: one per unsent invoice of the pair, so the
                  visible text names the invoice too (it is the accessible name,
                  WCAG 2.5.3). */}
              <button
                key={u.accountInvoiceId}
                type="button"
                class="btn btn--quiet btn--inline"
                disabled={busy}
                onClick={() => void onResend(u, unsent)}
              >
                Send invoice {u.invoiceNumber} to the home
              </button>
            </>
          ))}
        </Banner>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SECTION 1 — WHAT IS OWED. The requirement, and the reason this tab   */}
      {/* exists at all.                                                      */}
      {/* ------------------------------------------------------------------ */}
      <h2 class="screen-sub">{owe.heading}</h2>
      {data.outstanding.rows.length > 0 && (
        <p class="helper-note">
          {data.outstanding.rows.some((row) => row.invoices.some(owedOn)) ? owe.owed : owe.nothingOwed}
        </p>
      )}

      {data.outstanding.truncated && (
        /* ⛔ THE BOUND SAYING IT BIT. `warn`, not `error`: nothing failed, and
           calling a working screen broken is its own kind of wrong. */
        <Banner kind="warn">
          This list is longer than the screen can show, so some homes are not on it.
          Nothing has been deleted.
        </Banner>
      )}

      {data.outstanding.rows.length === 0 ? (
        <p class="helper-note">{owe.none}</p>
      ) : (
        <div class="inv-scroll" role="region" tabIndex={0} aria-label={`${owe.heading}, scrollable`}>
          <table class="book-table" aria-label={owe.heading}>
            <thead>
              <tr>
                {showHomeColumn && <th scope="col">Home</th>}
                <th scope="col" class="band-col">Owed</th>
                <th scope="col">Invoices</th>
              </tr>
            </thead>
            <tbody>
              {data.outstanding.rows.map((row) => (
                <>
                  <tr key={row.payerId}>
                    {/* ⛔ data-fb-opaque: the click-trail recorder stores the
                        accessible NAME of what was clicked, and `redactSensitive`
                        catches emails and long digit runs and NEVER names. A home
                        is an organisation, but it is still an identifiable party
                        in an Article 9 context. Same treatment the roster's
                        home-named controls already carry. */}
                    {showHomeColumn && <td class="place-col" data-fb-opaque>{row.payerName}</td>}
                    <td class="band-col">
                      {/* ≥24px: this is the figure Helen acts on. */}
                      <span class="owed-figure">{euro(row.outstandingCents)}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn--quiet btn--inline"
                        /* ⛔ NAMED PER ROW, so a screen-reader user tabbing the
                           table hears WHOSE invoices each control opens rather
                           than the same two words over and over. */
                        aria-label={`Show invoices for ${row.payerName}`}
                        aria-expanded={openPayer === row.payerId}
                        data-fb-opaque
                        onClick={() => {
                          setOpenPayer(openPayer === row.payerId ? null : row.payerId);
                          setPdfNote(null);
                        }}
                      >
                        {row.invoices.length === 1
                          ? '1 invoice'
                          : `${String(row.invoices.length)} invoices`}
                      </button>
                      {/* ⭐ board 2764 RW1 (R2) - said on the home's own row, so an
                          unsent invoice is visible without opening the list. The
                          Owed figure leaves it out (nobody has been asked for it),
                          and without this line a failed send would read as nothing
                          owed. A COUNT of the server's own `atStripe` facts, never a
                          money figure of this screen's making. */}
                      {row.invoices.filter(isUnsent).length > 0 && (
                        <span class="helper-note">
                          {' '}{String(row.invoices.filter(isUnsent).length)} not sent yet
                        </span>
                      )}
                    </td>
                  </tr>
                  {openPayer === row.payerId && (
                    <tr key={`${row.payerId}-open`}>
                      <td class="inv-list-cell" colSpan={showHomeColumn ? 3 : 2}>
                        {/* ⭐ board 2812 fix round (Captain's /check item 5): below 1280px
                            (beside the homes column) the Month column folds into the
                            Invoice cell, so the list fits with nothing clipped and no
                            sideways scroll (`app.css`, `.inv-list`). The DOM keeps its six
                            columns and the month its own cell; only the paint moves. */}
                        <table class="book-table inv-list" aria-label={`Invoices for ${row.payerName}`}>
                          <thead>
                            <tr>
                              <th scope="col">Invoice</th>
                              <th scope="col" class="inv-month">Month</th>
                              <th scope="col" class="inv-state">State</th>
                              <th scope="col" class="band-col">Amount</th>
                              {/* board 2764 M6 (R-12): was "Runs", the disclosure of
                                  ECMB's own table of the frozen lines. */}
                              <th scope="col">Stripe's copy</th>
                              {/* T1447 M6 — the recovery controls. */}
                              <th scope="col">What to do</th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.invoices.map((inv) => (
                              <>
                                <tr key={inv.accountInvoiceId}>
                                  {/* ⭐ board 2764 M7 (R-8) - THE SEGMENT IN THE NUMBER
                                      CELL ("INV-2026-00007 · HSE / Rehab"), no 7th
                                      column. Each row names ITS OWN kind, so the pair
                                      reads from the live rows; a companion number is
                                      never shown (it can name a withdrawn invoice). The
                                      segment is its own element so the number stays
                                      the cell's own text. */}
                                  <td class="place-col inv-number" data-month={periodWords(inv.year, inv.month)}>
                                    {/* ⭐ board 2812 fix round 2 (ux M3): the number never breaks
                                        (`.inv-number-text`), so the cell reads number, kind, month. */}
                                    <span class="inv-number-text">{inv.invoiceNumber}</span>
                                    {segmentOf(inv) !== null && <span class="helper-note"> · {segmentOf(inv)}</span>}
                                  </td>
                                  <td class="inv-month">{periodWords(inv.year, inv.month)}</td>
                                  <td>
                                    {/* ⭐ board 2764 RW1 (R2): an issued invoice
                                        Stripe never received reads as NOT SENT,
                                        never "Waiting to be paid". */}
                                    {stateWords(inv)}
                                    {/* ⭐⭐ T1447 M6 — THE ATTRIBUTION, DISPLAYED.
                                        This is what makes v37's voided_by/voided_at a
                                        COMPENSATING CONTROL rather than coverage: a
                                        withdrawal re-opens the booking-to-account link,
                                        and with ONE operator trust level any session can
                                        take it, so the act has to be answerable ON THE
                                        SCREEN and not only in the database.
                                        ⛔ RENDERED ONLY WHEN BOTH HALVES ARE PRESENT.
                                        NULL is not "unknown operator" — it means never
                                        withdrawn, or withdrawn before v37 existed — and
                                        "withdrawn by nobody" would be a fabrication.
                                        ⚠️ `!= null`, LOOSE, AND DELIBERATELY SO. A server
                                        that has not yet been redeployed omits the field
                                        entirely, and `undefined !== null` is TRUE — which
                                        would render the literal string "undefined" as an
                                        operator's name on a money screen.
                                        ⛔ data-fb-opaque: it carries an operator's name,
                                        exactly as issuedBy does. */}
                                    {inv.voidedBy != null && inv.voidedAt != null && (
                                      <span class="helper-note" data-fb-opaque>
                                        {' '}Withdrawn by {inv.voidedBy} on {shortDate(inv.voidedAt)}
                                      </span>
                                    )}
                                  </td>
                                  <td class="band-col">{euro(inv.cents)}</td>
                                  <td>
                                    {/* ⭐ board 2764 M6 (R-12, RULED D4) - STRIPE'S PDF,
                                        AND ONLY WHERE THERE IS ONE TO OPEN: sent
                                        (`atStripe`, the server's word) and not
                                        withdrawn. An unsent invoice has no Stripe
                                        copy and a withdrawn one is not offered by
                                        the route (409 `void`), so neither shows a
                                        control whose only outcome is a refusal.
                                        ⛔ THE HREF IS THIS KIOSK'S ROUTE, never a
                                        Stripe URL (the hosted page and
                                        `invoice_pdf` are bearer URLs). The click
                                        fetches instead of navigating so the first
                                        hour's 409 `not_ready` is WORDED, not
                                        downloaded as an error body.
                                        ⛔ THE ACCESSIBLE NAME IS KEYED ON THE INVOICE
                                        NUMBER, for the reason the recovery controls
                                        record beside them, and it STARTS with the
                                        visible text (WCAG 2.5.3, label in name).
                                        `btn--link` is the library's inline-link
                                        affordance.
                                        ⭐ board 2764 M7: the visible text is "PDF"
                                        under the "Stripe's copy" header. "Stripe's
                                        invoice (PDF)" wrapped to three lines in this
                                        narrow column (M6 screenshot 04); the
                                        accessible name still says whose PDF. */}
                                    {inv.atStripe && inv.status !== 'void' && (
                                      <a
                                        class="btn btn--link"
                                        href={accountInvoicePdfHref(inv.accountInvoiceId)}
                                        download={`invoice-${inv.invoiceNumber}.pdf`}
                                        aria-label={`PDF of Stripe's invoice ${inv.invoiceNumber}`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          void onOpenPdf(inv);
                                        }}
                                      >
                                        PDF
                                      </a>
                                    )}
                                  </td>
                                  <td>
                                    {/* ⛔ THE PANE'S OWN `.row-actions` WRAPPER, NOT A NEW
                                        CLASS AND NOT BARE INLINE BUTTONS. Unwrapped these
                                        three controls sat at a **0px** gap — sharing a
                                        border — and the mis-tap landed on **Withdraw**, the
                                        destructive one. `.row-actions` is the flex/gap
                                        rhythm the confirm rows below already use, and its
                                        `gap` applies on BOTH axes, so the wrapped case (a
                                        narrow column, buttons stacked) is spaced too. */}
                                    <div class="row-actions">
                                      {/* ⭐ board 2764 RW1 (R2) - SEND, ON THE ROW, so it
                                          survives a reload. Offered ONLY where the
                                          server says the invoice is issued and Stripe
                                          holds nothing (`atStripe` false): a nil row
                                          that reached Stripe is never offered it (a
                                          re-send re-emails the home, M5 security L4),
                                          and a sent invoice is refused `already_sent`.
                                          The existing re-send route; the accessible
                                          name names the invoice and starts with the
                                          visible word (WCAG 2.5.3), and differs from
                                          the notice's "... to the home" so the two
                                          never share a name. */}
                                      {isUnsent(inv) && (
                                        <button
                                          type="button"
                                          class="btn btn--quiet btn--inline"
                                          aria-label={`Send invoice ${inv.invoiceNumber}`}
                                          disabled={busy}
                                          onClick={() => void onResend(
                                            { accountInvoiceId: inv.accountInvoiceId, invoiceNumber: inv.invoiceNumber, isNil: inv.isNil },
                                            notice?.unsent ?? [],
                                          )}
                                        >
                                          Send
                                        </button>
                                      )}
                                      {/* ⛔⛔ A PERMANENTLY-REFUSED ACTION IS NOT RENDERED
                                          AT ALL, rather than rendered disabled. A `paid`
                                          row cannot be withdrawn (the service refuses it,
                                          and so does Stripe) and a `void` row cannot be
                                          withdrawn or marked paid — those refusals are
                                          PERMANENT for that document, and "a control that
                                          looks actionable and is not is the exact defect
                                          P0-6 records against this screen". Contrast the
                                          assembly card's not-over-yet case, which is
                                          TEMPORARY and dated and is therefore rendered
                                          disabled with its reason stated.
                                          ⛔ THE ACCESSIBLE NAME IS KEYED ON THE INVOICE
                                          NUMBER, never a bare "Withdraw": getByLabelText
                                          matches aria-label, account-list.spec.tsx queries
                                          getByLabelText('Billing email') at EIGHT call
                                          sites, and a colliding label reds all eight under
                                          strict mode. Keying on the number also keeps a
                                          customer's name out of an accessible name. */}
                                      {/* ⭐ board 2764 M7: never on a NIL invoice. It asks
                                          for nothing, so there is no payment to record.
                                          ⭐ RW1 round 2 (ux finding 6): nor on an invoice
                                          that was NOT SENT (`isUnsent`): nobody has been
                                          asked to pay it and it has no Stripe PDF, so the
                                          row offers Send first, then Withdraw, the nil
                                          unsent row's shape. */}
                                      {inv.status === 'issued' && !inv.isNil && !isUnsent(inv) && (
                                        <button
                                          type="button"
                                          class="btn btn--quiet btn--inline"
                                          aria-label={`Mark invoice ${inv.invoiceNumber} paid`}
                                          aria-expanded={openAction?.id === inv.accountInvoiceId && openAction.kind === 'paid'}
                                          disabled={busy}
                                          onClick={() => onToggleAction(inv.accountInvoiceId, 'paid')}
                                        >
                                          Mark paid
                                        </button>
                                      )}
                                      {inv.status === 'issued' && (
                                        <button
                                          type="button"
                                          class="btn btn--quiet btn--inline"
                                          aria-label={`Withdraw invoice ${inv.invoiceNumber}`}
                                          aria-expanded={openAction?.id === inv.accountInvoiceId && openAction.kind === 'void'}
                                          disabled={busy}
                                          onClick={() => onToggleAction(inv.accountInvoiceId, 'void')}
                                        >
                                          Withdraw
                                        </button>
                                      )}
                                      {/* ⚠️ OFFERED ON EVERY STATE, INCLUDING void AND
                                          paid, and that is deliberate: it is a READ, it
                                          changes nothing, and the question "what does
                                          Stripe still think?" is at its sharpest exactly
                                          on a document our ledger already calls settled or
                                          withdrawn. It is offered ONLY where the server
                                          said the document reached Stripe, so it is never
                                          a control whose only outcome is "nothing there".
                                          ⭐ board 2764 M7: NOT on a nil invoice (#21).
                                          Stripe settles it at EUR 0 when it is finalised,
                                          and its payment words ("can no longer pay this",
                                          "nothing down as paid") would read as alarming
                                          about a document that asks for nothing. */}
                                      {inv.atStripe && !inv.isNil && (
                                        <button
                                          type="button"
                                          class="btn btn--quiet btn--inline"
                                          aria-label={`Check invoice ${inv.invoiceNumber} at Stripe`}
                                          aria-expanded={openAction?.id === inv.accountInvoiceId && openAction.kind === 'stripe'}
                                          disabled={busy}
                                          onClick={() => onToggleAction(inv.accountInvoiceId, 'stripe')}
                                        >
                                          Check at Stripe
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {openAction?.id === inv.accountInvoiceId && (
                                  <tr key={`${String(inv.accountInvoiceId)}-action`}>
                                    <td colSpan={6}>
                                      {openAction.kind === 'void' && (
                                        <div class="row-actions">
                                          {/* ⛔ WHAT IT COSTS, BEFORE THE SECOND PRESS.
                                              A withdrawal is irreversible in the one way
                                              that matters — the number is spent for ever
                                              (the unique index on invoice_number is
                                              TOTAL) — and it UNFREEZES the run-to-account
                                              link, so the runs become billable again and
                                              can be moved to another home. Helen is told
                                              both, in her words. */}
                                          {inv.isNil ? (
                                            /* ⭐ board 2764 M7: a NIL invoice asks for nothing
                                               and Stripe cannot cancel it (it is settled at EUR
                                               0), so the generic sentence below would be false
                                               twice over. */
                                            <p class="queue-evidence">
                                              Withdrawing invoice {inv.invoiceNumber} takes it back. It
                                              asked for nothing, and its number stays used for ever, so
                                              the month's HSE / Rehab invoice would be issued under a
                                              new one. Stripe keeps its copy, marked as settled at no
                                              charge.
                                            </p>
                                          ) : (
                                            <p class="queue-evidence">
                                              Withdrawing invoice {inv.invoiceNumber} takes it back:
                                              the home is no longer being asked for {euro(inv.cents)},
                                              its runs can be billed again, and the number stays used
                                              for ever, so the month would be issued under a new one.
                                              {inv.atStripe
                                                ? ' It was sent to the home through Stripe, so that copy is cancelled first; if it had already been emailed, the email stays in their inbox but will not take payment.'
                                                : ' It was never sent to the home, so nothing has to be taken back from them.'}
                                            </p>
                                          )}
                                          {/* ⛔ btn--secondary on the confirm, so the pane
                                              keeps at most ONE dominant primary action
                                              (the top-up). The Issue control's shape,
                                              verbatim. */}
                                          <button
                                            type="button"
                                            class="btn btn--secondary"
                                            disabled={busy}
                                            aria-label={`Yes, withdraw invoice ${inv.invoiceNumber}`}
                                            onClick={() => {
                                              setOpenAction(null);
                                              void onVoid(inv);
                                            }}
                                          >
                                            Yes, withdraw it
                                          </button>
                                          <button
                                            type="button"
                                            class="btn btn--quiet btn--inline"
                                            disabled={busy}
                                            onClick={() => setOpenAction(null)}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      )}
                                      {openAction.kind === 'paid' && (
                                        <div class="row-actions">
                                          {/* ⛔⛔ THE OPERATOR TYPES WHAT ARRIVED. The
                                              amount is NOT pre-filled with inv.cents and
                                              must never be: the server compares the two
                                              EXACTLY, and two homes on identical
                                              contracts have identical monthly totals, so
                                              a pre-filled figure makes that comparison a
                                              tautology.
                                              ⛔ THE UNIT IS SPELLED OUT IN THE LABEL AND
                                              IN THE PLACEHOLDER. A figure typed into a
                                              field that silently means cents can MATCH by
                                              coincidence rather than reject. */}
                                          <label class="row-actions__field">
                                            <span>Amount the home paid, in euro (e.g. 1250.00)</span>
                                            {/* ⛔ WCAG 2.5.3 LABEL IN NAME: THE ACCESSIBLE NAME
                                                IS A SUPERSET OF THE VISIBLE TEXT, invoice number
                                                appended rather than substituted. It used to read
                                                "Amount received for invoice …", which shares NO
                                                phrase with the label above it, so a speech-input
                                                user saying "click Amount the home paid" matched
                                                nothing. ⚠️ axe-core does NOT catch this shape:
                                                `label-content-name-mismatch` targets
                                                text-content-labelled controls, not a label-wrapped
                                                input overridden by aria-label — it was found by
                                                reading, and it is pinned in
                                                account-invoices-tab.spec.tsx against the visible
                                                span's OWN text rather than against a literal. */}
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              autocomplete="off"
                                              placeholder="0.00"
                                              aria-label={`Amount the home paid, in euro (e.g. 1250.00), for invoice ${inv.invoiceNumber}`}
                                              value={paidAmount}
                                              disabled={busy}
                                              onInput={(e) => setPaidAmount((e.currentTarget).value)}
                                              // ⛔ FOCUS MOVES INTO THE FIELD WHEN THE PANEL OPENS.
                                              // Without it focus stayed on the now-`aria-expanded`
                                              // trigger and the NEXT TAB landed on **Withdraw** —
                                              // the destructive control — two tabs before the field
                                              // the operator was sent here to fill. Same hazard as
                                              // the 0px gap above, arriving by keyboard.
                                              // ⚠️ THE HOUSE IDIOM, VERBATIM (RateList.tsx,
                                              // QueueView.tsx, AddonsRules.tsx — all three added for
                                              // the same ux-review finding): focus ONCE per mount,
                                              // flagged on the element itself, so a re-render while
                                              // she is typing can never steal focus back.
                                              ref={(el) => { if (el && !el.dataset['autofocused']) { el.dataset['autofocused'] = '1'; el.focus(); } }}
                                            />
                                          </label>
                                          <p class="queue-evidence">
                                            This records money that arrived in the bank. It does not
                                            take a payment.
                                            {inv.atStripe
                                              // ⛔⛔ THE RESIDUAL, SAID BEFORE THE PRESS
                                              // AS WELL AS AFTER IT. Nothing in this
                                              // build stops the Stripe copy, and an
                                              // operator who is only told afterwards
                                              // learns it once the dunning has started.
                                              ? ' ⚠️ This invoice also went out through Stripe. Marking it paid here does NOT stop the copy the home holds: that page can still be paid and Stripe will keep chasing it. Tell Alex if it needs stopping.'
                                              : ''}
                                          </p>
                                          <button
                                            type="button"
                                            class="btn btn--secondary"
                                            disabled={busy}
                                            aria-label={`Yes, mark invoice ${inv.invoiceNumber} paid`}
                                            onClick={() => {
                                              setOpenAction(null);
                                              void onMarkPaid(inv);
                                            }}
                                          >
                                            Yes, mark it paid
                                          </button>
                                          <button
                                            type="button"
                                            class="btn btn--quiet btn--inline"
                                            disabled={busy}
                                            onClick={() => setOpenAction(null)}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      )}
                                      {openAction.kind === 'stripe' && (
                                        <>
                                          {stripeState === undefined && (
                                            <p class="helper-note" role="status">Asking Stripe about this invoice…</p>
                                          )}
                                          {stripeState === null && (
                                            <p class="helper-note">
                                              Stripe could not be reached, so there is nothing to report.
                                              This invoice is unchanged.
                                            </p>
                                          )}
                                          {stripeState != null && (
                                            <p class="queue-evidence">{stripeWords(stripeState)}</p>
                                          )}
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                )}
                                {pdfNote?.id === inv.accountInvoiceId && (
                                  <tr key={`${String(inv.accountInvoiceId)}-pdf`}>
                                    {/* 6: the "What to do" column (T1447 M6). */}
                                    <td colSpan={6}>
                                      <p class="helper-note" role="status">{pdfNote.text}</p>
                                    </td>
                                  </tr>
                                )}
                              </>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SECTION 2 — the month's assembly queue.                             */}
      {/* ------------------------------------------------------------------ */}
      {/* ⭐ board 2812 M5 (fix round 1, U4): the section gap, in the separator the
          booking detail uses between its sections (`.section-rule`); the owed table
          ended 0 to 4px above this heading. */}
      <hr class="section-rule" />
      <h2 class="screen-sub">Ready to invoice for {period}</h2>
      {/* ⛔⛔ THE MONTH IS NAMED IN WORDS, TWICE, AND THAT IS NOT DUPLICATION.
          This queue changes month on the 1st, on its own, with money on every
          card - a screen that quietly swaps which period it is totalling is
          worse than one that says so. The heading names it, and this line says
          WHY it is that month, so an operator who opens the tab on 1 September
          and sees August is told rather than left to infer. */}
      <p class="helper-note">
        {period} is the last full month, so it is the one to invoice now.
        {queueLine !== null && ` ${queueLine}`}
      </p>
      {!periodIsOver && (
        /* ⛔ THE REASON THE ISSUE BUTTONS BELOW ARE DISABLED, SAID ONCE. It is a
           property of the MONTH, not of any one home, so repeating it on every
           card would be the same sentence N times on a pane whose whole design
           rule is that it stays scannable. A part-month document spends the
           payer's one live slot for that month and strands the rest, which is
           why the service refuses it and why there is no override. */
        <p class="helper-note">
          {/* ⭐ board 2812 fix round 2 (ux L4): "raised", never "invoiced". */}
          {period} is not over yet, so no invoice can be raised for it. Invoices
          can be raised from {issuableFromWords}.
        </p>
      )}

      {data.assembly.truncated && (
        <Banner kind="warn">
          There is more work this month than the screen can show, so some homes are
          not on this list. Nothing has been deleted.
        </Banner>
      )}

      {data.assembly.rows.length === 0 ? (
        /* ⭐ T1749 M9 (DS-25) — THE LIBRARY'S `EmptyState` (§7.1), the second and
           last `.queue-card--empty` site; the class retires with it and its
           legacy entry arms. Both sentences verbatim, and the period is still
           NAMED in each: "nothing is waiting" without saying WHICH month is the
           reassurance this screen must never give.
           ⛔ T1749 M9 rework 2 — NEUTRAL, which is now the default and is why no
           tone is passed. The earlier note argued the green on "a true zero, not
           rows hidden behind a filter", and that is the wrong test: the question
           is whether the emptiness is GOOD NEWS. "No run travelled for a customer
           account in this period" is a month with no account trade — nobody's
           good news, and not a queue Helen has cleared. */
        <EmptyState
          headline={`Nothing is waiting for ${period}.`}
          evidence={`No run travelled for a customer account in ${period}.`}
        />
      ) : (
        data.assembly.rows.map((row) => (
          <div key={row.payerId} class={offersIssue(row) ? 'queue-card queue-card--gold' : 'queue-card'}>
            <p class="queue-headline" data-fb-opaque>{row.payerName}</p>
            <p class="queue-evidence">
              {row.runCount === 1 ? '1 run' : `${String(row.runCount)} runs`}
              {', '}
              {shortDate(row.firstRun)} to {shortDate(row.lastRun)}
              {row.siteCount > 1 && `, across ${String(row.siteCount)} homes`}
              {row.payerSource === 'billing-party' && ', billed to the parent organisation'}
              {/* ⭐ board 2764 M7: said only when NOTHING is left to issue. After a
                  void of one kind the month is still "invoiced" in the other, and
                  the card offers Issue for the missing kind. */}
              {/* ⭐ board 2812 fix round 2 (ux L4): "raised", and whether it is sent,
                  never "invoiced" (which reads as sent). */}
              {row.alreadyInvoiced && nothingLeft(row)
                && raisedSentence(KINDS.filter((k) => row.kinds[k].live).map((k) => raisedStateOf(row, k, data.outstanding.rows, data.year, data.month)))}
            </p>
            {/* ⭐ board 2764 RW1 (R1) - A PAYER WITH NO BILLING EMAIL IS TOLD SO,
                BEFORE ANY PRESS, and offered no Issue (`offersIssue`). The issue
                refuses it before a number is taken; the remedy is Helen's. */}
            {namesUnavailable(row) && !nothingLeft(row) && (
              <p class="queue-evidence">{NAMES_UNAVAILABLE_WORDS}</p>
            )}
            {lacksBillingEmail(row) && !nothingLeft(row) && (
              <p class="queue-evidence">
                {noBillingEmailWords(accountName(row.anchorAccountId) ?? `account ${String(row.anchorAccountId)}`, row.siteCount)}
              </p>
            )}
            {/* ⭐ board 2764 M7 (R-8) - WHAT EACH INVOICE OF THE PAIR HOLDS, one
                line per kind, and the runs a press would NOT bill, each with its
                remedy. The words are this screen's; the counts are the roll-up's. */}
            {kindLines(row, (k) => raisedStateOf(row, k, data.outstanding.rows, data.year, data.month))
              .map((line) => <p key={line} class="queue-evidence">{line}</p>)}
            {row.noPayerKindCount > 0 && (
              <p class="queue-evidence">
                {/* ⭐ RW1 round 2 (ux finding 1): with no billing email the card
                    offers no Issue, so the line never points at a button that is
                    not there; it says when Issue will list them. */}
                {runsWord(row.noPayerKindCount)} with no payer set yet.{' '}
                {lacksBillingEmail(row) ? 'Once the billing email is set, Issue lists' : 'Issue lists'}{' '}
                {row.noPayerKindCount === 1 ? 'it' : 'them'} so you can choose who pays.
              </p>
            )}
            {lateLines(row).map((line) => <p key={line} class="queue-evidence">{line}</p>)}
            <p class="queue-total">
              <span class="owed-figure">{euro(row.cents)}</span>
              {/* ⛔ THE TOTAL SAYS IT IS INCOMPLETE RATHER THAN ABSORBING THE
                  UNPRICED RUNS AS ZERO. A zero positively asserts the run was
                  free, on a figure an operator is about to bill from. */}
              {row.unpricedCount > 0 && (
                <span class="helper-note">
                  {' '}plus {String(row.unpricedCount)} run(s) nobody has priced yet, so this
                  total is not the whole month.
                </span>
              )}
            </p>
            {confirmIssue === row.payerId && (
              /* ⛔⛔ THE TWO-STEP REVEAL, AND WHAT MAKES IT NECESSARY IS THAT
                 NOTHING UNDOES A MIS-TAP. `voidIssuedAccountInvoice` exists in
                 core and HAS NO ROUTE, so an accidental issue cannot be taken
                 back from inside the product; issuing FREEZES the runs, which M6
                 then makes uncancellable (`BookingInvoicedNotCancellableError`
                 names void-and-reissue as the remedy - a remedy with no route);
                 and it sends a real invoice to a nursing home. The mockup drew a
                 bare button, but it predates both of those findings. This is the
                 SAME shape the "Stop contract" card below already uses, so the
                 pane gains no new idiom.
                 ⚠️ THE CONFIRM STAYS `btn--secondary`: at most ONE dominant
                 primary action on this pane, and it is the top-up.
                 ⭐ board 2764 M7: ONE confirm for the pair, asked as a question
                 with the server's count ("Issue 2 invoices for September 2026?").
                 ⚡ The old "Nothing on this screen takes it back" stopped being
                 true at T1447 M6 (Withdraw); the sentence now says what does. */
              <p class="queue-evidence">{issueConfirmWords(row, period)}</p>
            )}
            <div class="queue-actions">
              {/* ⛔ btn--secondary, so the pane keeps at most ONE dominant primary
                  action (the top-up below). */}
              <button
                type="button"
                class="btn btn--secondary"
                aria-label={`Show what makes up ${row.payerName}'s total`}
                aria-expanded={openQueue === row.payerId}
                data-fb-opaque
                onClick={() => {
                  setOpenQueue(openQueue === row.payerId ? null : row.payerId);
                  setCorrection(null);
                }}
              >
                What is in this
              </button>
              {/* ⛔ A PERIOD WITH NOTHING LEFT TO ISSUE IS NOT OFFERED THE ACTION
                  AT ALL, rather than offered it disabled. The evidence line above
                  already says the month has been invoiced, the refusal is
                  PERMANENT for that payer-period until an invoice is withdrawn,
                  and a control that looks actionable and is not is the exact
                  defect P0-6 records against this screen. Contrast the
                  not-over-yet case, which is TEMPORARY and dated, so it is
                  rendered disabled with its reason stated once above.
                  ⭐ board 2764 M7: "nothing left" is the server's `kindsToIssue`
                  (plus runs with no payer, which only a press can name), never
                  `alreadyInvoiced`: after a void of ONE kind the month is still
                  invoiced in the other and the missing kind is issuable. */}
              {offersIssue(row) && (confirmIssue === row.payerId ? (
                <>
                  <button
                    type="button"
                    class="btn btn--secondary"
                    disabled={busy}
                    aria-label={`Yes, issue the invoices for ${row.payerName} for ${period}`}
                    data-fb-opaque
                    onClick={() => {
                      setConfirmIssue(null);
                      void onIssue(row, monthKey, period);
                    }}
                  >
                    Yes, issue
                  </button>
                  <button
                    type="button"
                    class="btn btn--quiet btn--inline"
                    disabled={busy}
                    onClick={() => setConfirmIssue(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  class="btn btn--secondary"
                  /* ⛔ NAMED PER ROW, and therefore `data-fb-opaque`: the
                     accessible name has to disambiguate WHICH home is about to
                     be invoiced, and the click-trail recorder stores that name.
                     A home is an organisation and still an identifiable party in
                     an Article 9 context. */
                  disabled={busy || !periodIsOver}
                  aria-label={`Issue the invoices for ${row.payerName} for ${period}`}
                  data-fb-opaque
                  /* ⛔ THE ATTRIBUTE GATES THE PIXEL; THIS GATES THE DECISION.
                     `disabled` stops a mouse, and it stopped nothing when the
                     grading spec dispatched a click straight at the element -
                     the confirm opened on a month the service cannot issue.
                     Nothing was posted (the confirm is a second, separate
                     press), but the reveal is where the guard belongs. */
                  onClick={() => { if (periodIsOver) setConfirmIssue(row.payerId); }}
                >
                  Issue
                </button>
              ))}
            </div>
            {refusal?.payerId === row.payerId && (
              <PayerRefusalList refusal={refusal} busy={busy} onSetPayer={onSetPayer} />
            )}
            {openQueue === row.payerId && (
              <>
                <p class="queue-evidence">
                  {/* ⛔ THE ANCHOR SITE'S ID, NOT A SECOND MONEY FIGURE. This pane
                      shows the PAYER's total; naming a per-site amount beside it is
                      exactly the site-versus-payer confusion the roll-up exists to
                      remove. */}
                  {/* ⭐ board 2812 fix round (item 7): "For", never "Raised against",
                      which read as an invoice raised on a card with none. */}
                  For account {String(row.anchorAccountId)}
                  {accountName(row.anchorAccountId) !== null && `, ${accountName(row.anchorAccountId) ?? ''}`}
                  {'. '}
                  Every run counted here already travelled before today.
                </p>
                {/* ⭐ board 2764 RW1 (R3) - WHO PAYS FOR EACH RUN NOT YET ON AN
                    INVOICE, with one quiet control to correct it, behind the
                    disclosure that already exists (no new screen, no new rail
                    row). */}
                {/* ⭐ board 3059: an ON payer's preview says so, once, above the runs
                    whose lines will carry the names. */}
                {row.residentNames === 'on' && (
                  <p class="queue-evidence">
                    Resident names are on for this payer, so each invoice line will show the name below.
                  </p>
                )}
                <UninvoicedRunList
                  runs={row.uninvoicedRuns ?? []}
                  issueOffered={!lacksBillingEmail(row)}
                  busy={busy}
                  onCorrect={(run, kind) => onCorrectPayer(row.payerId, run, kind)}
                />
                {correction?.payerId === row.payerId && (
                  <p class="queue-evidence" role="status">{correction.text}</p>
                )}
              </>
            )}
          </div>
        ))
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SECTION 3 — contracts that are under-billing, which is a SILENT      */}
      {/* failure and therefore the one worth alarming on.                     */}
      {/* ------------------------------------------------------------------ */}
      <h2 class="screen-sub">Contracts to check</h2>
      {data.shortfalls.length === 0 ? (
        <p class="helper-note">Every recurring contract is putting its runs in the diary.</p>
      ) : (
        data.shortfalls.map((s) => (
          <ShortfallCard
            key={s.seriesId}
            shortfall={s}
            homeName={accountName(s.accountId)}
            busy={busy}
            onStop={onStopSeries}
          />
        ))
      )}
      {/* ⛔ THREE STATES, NOT TWO, AND THE THIRD IS WHY THIS IS HERE (ux-review
          U3). `undefined` is the parent's read STILL RUNNING and `null` is that
          read having FAILED. The card above falls back to `account 42` in both,
          so with only the `null` arm a warning briefly read "account 42" with no
          explanation at all — the one state on this pane where a number appears
          unaccounted for. This pane is otherwise scrupulous about keeping a
          failure and an absence apart; so is this. */}
      {accounts === undefined && (
        <p class="helper-note" role="status">
          Still getting the customer accounts, so the warnings above name account
          numbers for the moment.
        </p>
      )}
      {accounts === null && (
        <p class="helper-note">
          Could not read the customer accounts, so the warnings above name account
          numbers rather than homes.
        </p>
      )}

      {/* ⛔ THE ONE DOMINANT PRIMARY ACTION ON THIS PANE, and its copy must not
          overclaim. Capture is what puts a new contract's runs in the diary; this
          is the lever for the current and future months when something went
          wrong. It cannot fill in a month that has already gone, because runs are
          never materialised on a date that has already passed. */}
      {/* ⭐ board 2812 M4 fix round (L1): QUIET, not gold. M3's rule is that a screen's
          gold is its NEXT STEP, and the Invoices tab has none at rest: topping the diary
          up is a repair lever, not the job. Measured at 1024x700 on the seat, this gold
          sat at about 906px, below the plane, on a home's Invoices tab. */}
      <div class="op-cred-actions">
        <button type="button" class="btn btn--quiet" disabled={busy} onClick={() => void onTopUp()}>
          Bring the diary up to date
        </button>
      </div>
    </div>
  );
}

/**
 * ⭐ board 2764 M7 (R-5 UI) - THE RUNS A `payer_kind_missing` REFUSAL NAMED, each
 * with the two payer buttons, under the card that was pressed.
 *
 * ⛔ AT MOST `REFUSAL_RUNS_SHOWN` RUNS, so at most 20 buttons: the rest are
 * counted, and Helen sets these and issues again, which names the next ones.
 * ⛔ CATALOG `Button`s, quiet and inline, and the admin screens' existing row
 * grammar (`.op-list` / `.op-row` / `.op-name`): no new class. Each button's
 * accessible name STARTS with its visible label and names the run (WCAG 2.5.3),
 * by job reference and date, never a resident: the refusal carries neither a
 * name nor a destination.
 */
function PayerRefusalList({
  refusal,
  busy,
  onSetPayer,
}: {
  refusal: PayerRefusal;
  busy: boolean;
  onSetPayer: (run: ApiRunWithoutPayer, kind: ApiInvoiceKind) => Promise<void>;
}) {
  const shown = refusal.runs.slice(0, REFUSAL_RUNS_SHOWN);
  const more = refusal.total - shown.length;
  return (
    <>
      <p class="queue-evidence" role="status">
        These runs have no payer set. Choose who pays for each, then issue again.
      </p>
      <ul class="op-list" aria-label="Runs with no payer set">
        {shown.map((run) => {
          const which = `${run.jobRef ?? `booking ${String(run.bookingId)}`} on ${civilDateLabel(run.journeyDate, null)}`;
          return (
            <li key={run.bookingId} class="op-row">
              <span class="op-name">{civilDateLabel(run.journeyDate, null)}, {run.jobRef ?? `booking ${String(run.bookingId)}`}</span>
              {run.chosen !== undefined ? (
                <span class="helper-note">{kindLabel(run.chosen)} pays for this run.</span>
              ) : (
                KINDS.map((k) => (
                  <Button
                    key={k}
                    size="inline"
                    disabled={busy}
                    ariaLabel={`${kindLabel(k)} pays for ${which}`}
                    onClick={() => void onSetPayer(run, k)}
                  >
                    {kindLabel(k)}
                  </Button>
                ))
              )}
            </li>
          );
        })}
      </ul>
      {more > 0 && (
        <p class="queue-evidence">
          and {String(more)} more: set these first, then issue again.
        </p>
      )}
    </>
  );
}

/**
 * ⭐ board 2764 RW1 (R3) - THE MONTH'S RUNS THAT NO LIVE INVOICE CARRIES, each
 * with who pays for it and ONE quiet control to switch it to the other payer.
 *
 * ⛔ ONE CONTROL PER RUN, AND NONE ON A RUN WITH NO PAYER: correcting is HSE /
 * Rehab to house or back, so one button names the other side. A run with no
 * payer yet is a choice of two, which the R-5 refusal already offers at Issue
 * (`PayerRefusalList`); repeating its two buttons here would double the controls
 * on the card ("too many buttons" is the history, #21).
 * ⛔ CATALOG `Button`s, quiet and inline, in the admin screens' existing row
 * grammar (`.op-list` / `.op-row` / `.op-name`), as `PayerRefusalList`: no new
 * class. A run on a live invoice is not listed at all (the server leaves it
 * out): the route would refuse it, so a control there could only be refused.
 * Each accessible name starts with the visible label and names the run by job
 * reference and date, never a resident.
 * ⚠️ board 3059: for a payer whose resident-names switch is ON, each row's TEXT
 * also prints the resident's name (or "resident not recorded" and why), the
 * preview of what its invoice line will say. It is served for this render and
 * held in no state of its own; like every screen that renders a name it is a
 * DOM-capture sink for the build-gated feedback widget (the census in
 * test/core/resident-name-sink-census.spec.ts records that residual).
 */
function UninvoicedRunList({
  runs,
  issueOffered,
  busy,
  onCorrect,
}: {
  runs: ApiUninvoicedRun[];
  /** RW1 round 2 (ux finding 1): false while the payer has no billing email, so
   *  a run with no payer is not told that a hidden Issue lists it. */
  issueOffered: boolean;
  busy: boolean;
  onCorrect: (run: ApiUninvoicedRun, kind: ApiInvoiceKind) => Promise<void>;
}) {
  if (runs.length === 0) {
    return <p class="queue-evidence">Every run counted here is on an invoice already.</p>;
  }
  return (
    <>
      <p class="queue-evidence">Not on an invoice yet, and who pays for each:</p>
      <ul class="op-list" aria-label="Who pays for each run">
        {runs.map((run) => {
          const ref = run.jobRef ?? `booking ${String(run.bookingId)}`;
          const when = civilDateLabel(run.journeyDate, null);
          const other: ApiInvoiceKind = run.payerKind === 'hse' ? 'house' : 'hse';
          return (
            <li key={run.bookingId} class="op-row">
              <span class="op-name">
                {/* ⭐ board 3059: `{date}, {ref}, {label}: {kind}` for an ON payer's run. */}
                {when}, {ref}{run.residentLabel !== undefined && `, ${residentLabelWords(run.residentLabel)}`}: {run.payerKind !== null
                  ? kindLabel(run.payerKind)
                  : issueOffered ? 'no payer set yet, Issue lists it' : 'no payer set yet, Issue lists it once the billing email is set'}
              </span>
              {run.payerKind !== null && (
                <Button
                  size="inline"
                  disabled={busy}
                  ariaLabel={`Change to ${kindLabel(other)} for ${ref} on ${when}`}
                  onClick={() => void onCorrect(run, other)}
                >
                  Change to {kindLabel(other)}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * One under-billing contract, with the remedy it actually has.
 *
 * ⛔ NO CONTROL THAT LOOKS ACTIONABLE AND IS NOT (P0-6). A stop is the recorded
 * remedy for a DUPLICATE contract and for nothing else on this list — the other
 * three refusals are fixed by correcting the booking, and offering a stop there
 * would invite an operator to end a contract that is still running.
 *
 * ⛔⛔ THE STOP IS A TWO-STEP REVEAL, AND THE REASON IS THAT NOTHING UNDOES IT
 * (ux-review U1). `setBookingSeriesActive` takes `active: true`, so the ledger
 * can be restarted — but the ONLY client call site passes `false`, so from
 * inside the product a mis-tap ends a live recurring contract and there is no
 * screen anywhere that turns it back on. That is a psql prompt away from Helen,
 * on a control that sat one click from a warning card. The reveal is the SAME
 * shape the roster already uses for "Add a nurse at X" (a quiet control that
 * swaps itself for a confirm plus a Cancel), so the pane gains no new idiom.
 * ⚠️ THE CONFIRM STAYS `btn--secondary`: this pane keeps at most ONE dominant
 * primary action and it is the top-up.
 */
function ShortfallCard({
  shortfall,
  homeName,
  busy,
  onStop,
}: {
  shortfall: ApiSeriesShortfall;
  homeName: string | null;
  busy: boolean;
  onStop: (seriesId: number, name: string) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const name = homeName ?? `account ${String(shortfall.accountId)}`;
  const words = SHORTFALL_WORDS[shortfall.reason] ?? shortfall.detail;
  return (
    <div class="queue-card">
      <p class="queue-headline" data-fb-opaque>{name}</p>
      <p class="queue-evidence">
        {words}
        {/* ⛔ THE TWO COUNTS AND NO CORRECTED FIGURE. No per-occurrence data
            exists for runs that were never created, so "what it should have
            been" would be a recurrence pattern expanded into money, which is the
            arithmetic that billed a home four times what it owed. */}
        {shortfall.reason === 'missing-runs' && (
          <>
            {' '}The pattern says {String(shortfall.expected)} run(s) between today and the
            end of the month, and the diary holds {String(shortfall.actual)}.
          </>
        )}
      </p>
      {shortfall.reason === 'duplicate-pattern' && (confirming ? (
        <>
          <p class="queue-evidence">
            This stops contract {String(shortfall.seriesId)} putting any more runs in the
            diary. Runs already in the diary are left alone, and nothing on this screen
            turns it back on.
          </p>
          <div class="queue-actions">
            <button
              type="button"
              class="btn btn--secondary"
              disabled={busy}
              aria-label={`Yes, stop contract ${String(shortfall.seriesId)} for ${name}`}
              data-fb-opaque
              onClick={() => {
                setConfirming(false);
                void onStop(shortfall.seriesId, name);
              }}
            >
              Yes, stop it
            </button>
            <button
              type="button"
              class="btn btn--quiet btn--inline"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div class="queue-actions">
          <button
            type="button"
            class="btn btn--secondary"
            disabled={busy}
            aria-label={`Stop the duplicate contract ${String(shortfall.seriesId)} for ${name}`}
            data-fb-opaque
            onClick={() => setConfirming(true)}
          >
            Stop contract {String(shortfall.seriesId)}
          </button>
        </div>
      ))}
    </div>
  );
}
